/**
 * Der Arbeitsplatz: Karte links, Eingaben rechts.
 *
 * Der Leitgedanke der Spec steht hier im Aufbau: Alles, was sich aus der
 * Geometrie ableiten laesst, wird nicht abgefragt. Eingegeben wird nur, was das
 * Luftbild nicht hergibt — Neigung, Bauart des Dachs, Leitungswege.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import {
  azimutAusTraufkante,
  ordneKanteZu,
  bezugspunkt,
  nachGeo,
  nachGrundriss,
  nachGrundrissebene,
  ringIstBrauchbar,
  streckeM,
  begradige,
  ringNachGeo,
  ringNachGrundriss,
  schattenZuZeitpunkt,
  verschattungsstunden,
  abschlagAusVerschattung,
  type Geo,
  type Kalkulationsregeln,
  type Verschattungsobjekt,
  MINDESTFLAECHE_HINDERNIS_M2,
  wische,
} from '@pv/geometrie';

import { db, neueId, speichere } from '../daten/db.ts';
import { modulfarben, rechneAnlage } from '../anlage.ts';
import { dachstrecke, modulfelderVon, rechne, summiere, traegerFuer, type Planung } from '../planung.ts';
import { tagVon } from '../datum.ts';
import { rechneErtrag } from '../ertragrechnung.ts';
import { loeseAuf, VORGABEN, type Einstellungsquelle } from '../einstellungen.ts';
import { schlageFlaechenVor } from '../vorschlag.ts';
import { adressvarianten } from '../adresse.ts';
import { anmeldung } from '../daten/abgleich.ts';
import { objekteAusGelaende } from '../daten/hoehendaten.ts';
import {
  alsSatz,
  bereiteProjektVor,
  type Fortschritt,
  type Vorladeregeln,
} from '../daten/vorbereiten.ts';
import type {
  Angebotszeile,
  Belegung,
  Flaeche,
  Modul,
  Preisannahmen,
  Projekt,
  Rasterregelsatz,
  Stringzeile,
  Projekteinstellung,
  Verschattungsobjektzeile,
  Kartenmarke,
  Kartenmarkenart,
} from '../typen.ts';
import { Karte, MARKENNAMEN, type Kartenmodus, type Kartenquelle } from './Karte.tsx';
import { Flaechenpanel } from './Flaechenpanel.tsx';
import { Arbeitsmenue, Werkzeugleiste } from './Werkzeugleiste.tsx';

/** In diesen Modi zeigt die Karte nur das Arbeitsmenue, sonst nichts. */
const ARBEITSMODI: Kartenmodus[] = [
  'traufkante',
  'zeichnen',
  'aussparung',
  'modulfeld',
  'fassade',
  'verschattung-zeichnen',
  'bearbeiten',
  'umrisse',
  'module',
  'bemassung',
  'markierung',
];
import { Fassadenansicht } from './Fassadenansicht.tsx';
import { Fotos } from './Fotos.tsx';
import { Anlagepanel } from './Anlagepanel.tsx';
import { Materialpanel } from './Materialpanel.tsx';
import { Stringplan } from './Stringplan.tsx';
import { Wirtschaftlichkeitspanel } from './Wirtschaftlichkeitspanel.tsx';
import { Angebotspanel } from './Angebotspanel.tsx';
import { Angebotsdruck } from './Angebotsdruck.tsx';
import { Abwicklungspanel } from './Abwicklungspanel.tsx';
import { Ablaufpanel } from './Ablaufpanel.tsx';
import { Beschreibungsexport } from './Beschreibungsexport.tsx';
import { Dokumentation } from './Dokumentation.tsx';
import { Anlagendokumentation } from './Anlagendokumentation.tsx';
import { Zahlfeld } from './Zahlfeld.tsx';

interface Eigenschaften {
  projektId: string;
  quellen: Kartenquelle[];
  vorladen: Vorladeregeln;
  online: boolean;
  offeneAenderungen: number;
  /** Die Rolle des Angemeldeten. Die Montage sieht die Planung, aendert sie aber nicht. */
  rolle: string;
  /** Mit diesem Schritt beginnen — eine Erinnerung öffnet die Abwicklung. */
  startReiter?: Reiter;
  onZurueck: () => void;
}

export type Reiter =
  | 'objekt'
  | 'dach'
  | 'module'
  | 'anlage'
  | 'material'
  | 'ertrag'
  | 'angebot'
  | 'abwicklung'
  | 'fotos';

/**
 * Die Reiter als Arbeitsschritte, in der Reihenfolge einer Aufnahme.
 *
 * Vorbild ist K2 Base, das in fuenf Schritten vom Standort zum Ergebnis
 * fuehrt. Vorher stand im ersten Reiter "Flaechen" alles untereinander —
 * Kunde, Adresse, Vorbereiten, Neigung, Traufe, Referenzmass, Modul, Raender,
 * Lage, Masse —, und wer die Raender suchte, scrollte durch die halbe
 * Aufnahme (Rueckmeldung aus dem ersten Test bei Wania: "bessere
 * Menuestrukturen"). Abwicklung und Fotos gehoeren zu keinem Schritt und
 * stehen deshalb ohne Nummer daneben.
 */
const SCHRITTE: Array<[Reiter, string]> = [
  ['objekt', 'Objekt'],
  ['dach', 'Dach'],
  ['module', 'Module'],
  ['anlage', 'Elektrik'],
  ['material', 'Material'],
  ['ertrag', 'Ertrag'],
  ['angebot', 'Angebot'],
];
const NEBENREITER: Array<[Reiter, string]> = [
  ['abwicklung', 'Abwicklung'],
  ['fotos', 'Fotos'],
];

export function Projektansicht({
  projektId,
  quellen,
  vorladen,
  online,
  offeneAenderungen,
  rolle,
  startReiter,
  onZurueck,
}: Eigenschaften): React.JSX.Element {
  /**
   * Die Montage sieht die Planung, aendert sie aber nicht, und sieht keine
   * Einkaufspreise (Wunsch von Mathias, 11/2026). Durchgesetzt wird das am
   * Server; hier faellt nur weg, was dann ohnehin abgewiesen wuerde.
   */
  const nurLesen = rolle === 'monteur';
  // undefined heisst "laedt noch", null heisst "gibt es nicht" — sonst stand
  // bei jedem Oeffnen kurz "Projekt nicht gefunden" da.
  const projekt = useLiveQuery(async () => (await db.projekte.get(projektId)) ?? null, [projektId]);
  const flaechen = useLiveQuery(
    () =>
      db.flaechen
        .where('projekt_id')
        .equals(projektId)
        .filter((f) => !f.geloescht_am)
        .toArray(),
    [projektId],
    [] as Flaeche[],
  );
  // Nur die Belegungen dieses Projekts. Vorher kamen alle des Geräts mit — bis
  // in den eingefrorenen Stand jedes Angebots.
  const alleBelegungen = useLiveQuery(() => db.belegungen.toArray(), [], [] as Belegung[]);
  const belegungen = useMemo(() => {
    const ids = new Set(flaechen.map((f) => f.id));
    return alleBelegungen.filter((b) => ids.has(b.flaeche_id));
  }, [alleBelegungen, flaechen]);
  // Alle Module, nicht nur die aktiven: Wird ein Modul im Pool abgeschaltet,
  // rechnet eine Planung, die es schon nutzt, weiter damit — vorher fiel sie
  // still auf null Module. Zur Auswahl stehen nur die aktiven (Flächenpanel).
  const module = useLiveQuery(() => db.module.filter((m) => !m.geloescht_am).toArray(), [], [] as Modul[]);
  const gebaeude = useLiveQuery(
    () => db.gebaeudeumrisse.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const strings = useLiveQuery(
    () => db.strings.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [] as Stringzeile[],
  );
  const projektkomponenten = useLiveQuery(
    () => db.projekt_komponenten.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const leitungswege = useLiveQuery(
    () => db.leitungswege.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const materialpositionen = useLiveQuery(
    () => db.materialpositionen.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const verschattungsobjekte = useLiveQuery(
    () => db.verschattungsobjekte.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [] as Verschattungsobjektzeile[],
  );
  const kartenmarken = useLiveQuery(
    () => db.kartenmarken.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [] as Kartenmarke[],
  );
  const wirtschaftlichkeitsdaten = useLiveQuery(
    () => db.wirtschaftlichkeit.where('projekt_id').equals(projektId).first(),
    [projektId],
  );
  const angebote = useLiveQuery(
    () => db.angebote.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [] as Angebotszeile[],
  );
  const auftraege = useLiveQuery(
    () => db.auftraege.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const pruefbefunde = useLiveQuery(
    () => db.pruefbefunde.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const projektschritte = useLiveQuery(
    () => db.projektschritte.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const dokumente = useLiveQuery(
    () => db.dokumente.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const vorlagen = useLiveQuery(() => db.checklistenvorlagen.toArray(), [], []);
  const globaleEinstellungen = useLiveQuery(() => db.einstellungen.toArray(), [], []);
  const projekteinstellungen = useLiveQuery(
    () => db.projekteinstellungen.where('projekt_id').equals(projektId).toArray(),
    [projektId],
    [],
  );
  const komponenten = useLiveQuery(() => db.komponenten.toArray(), [], []);
  const artikel = useLiveQuery(() => db.artikel.toArray(), [], []);
  const materialsaetze = useLiveQuery(() => db.materialsaetze.toArray(), [], []);

  const [aktiveId, setAktiveId] = useState<string | null>(null);
  const [aktiverStringId, setAktiverStringId] = useState<string | null>(null);
  const [modus, setModus] = useState<Kartenmodus>('ansehen');
  const [entwurf, setEntwurf] = useState<Geo[]>([]);
  /**
   * Zuruecknehmen und wieder vorwaerts. Beim Zeichnen wandert der letzte Punkt
   * hierher statt verloren zu gehen; ein neuer Punkt leert den Stapel wieder.
   * Ohne das war jeder Fehlklick endgueltig — und beim Zeichnen verklickt man
   * sich staendig.
   */
  const [vorwaerts, setVorwaerts] = useState<Geo[]>([]);
  /** Der Ring, der gerade bearbeitet wird, samt Herkunft und Verlauf. */
  const [bearbeitung, setBearbeitung] = useState<{
    art: 'flaeche' | 'verschattung' | 'hindernis' | 'modulfeld';
    id: string;
    /** Beim Hindernis: welches der Fläche `id`. */
    index?: number;
    ring: Geo[];
    verlauf: Geo[][];
    vorwaerts: Geo[][];
  } | null>(null);
  const [gewaehlteEcke, setGewaehlteEcke] = useState<number | null>(null);
  /** Bemaßung oder Markierung, die gerade gesetzt oder geändert wird. */
  const [markeInArbeit, setMarkeInArbeit] = useState<{
    id: string | null;
    art: Kartenmarkenart;
    text: string;
  } | null>(null);
  /** Welche Kante in der Seitenleiste zum Einmessen gewaehlt ist (PV-D05). */
  const [referenzkante, setReferenzkante] = useState<number | null>(null);
  /** Welches Hindernis der aktiven Fläche rechts bearbeitet wird. */
  const [gewaehltesHindernis, setGewaehltesHindernis] = useState<number | null>(null);
  /** Worauf die Karte zoomen soll — ein neuer Ring zoomt neu. */
  const [zoomAuf, setZoomAuf] = useState<Geo[] | null>(null);

  /**
   * Versatz der Gebäudeumrisse gegenüber dem Luftbild (PV-D18).
   *
   * Der Umriss beschreibt den Grundriss am Boden, das Luftbild zeigt das Dach.
   * Ein Dachvorsprung macht einen halben Meter, und im Orthofoto kippt ein
   * hohes Haus zusätzlich nach außen. Dieser Anteil ist in einer Ortschaft
   * überall derselbe, also wird er je Projekt einmal eingestellt.
   *
   * Was er nicht ausgleicht, ist ein einzelnes Haus, das falsch erfasst ist.
   * Am 14.09.2026 gemessen: Bei OSM-Umrissen bringt eine gemeinsame
   * Verschiebung den mittleren Fehler von 0,91 auf 0,70 m und das
   * 90. Perzentil gar nicht. Deshalb kommen die Umrisse jetzt aus dem
   * amtlichen Kataster — und was dann noch danebensitzt, gehört einzeln
   * gezogen, nicht global.
   */
  const versatzzeile = projekteinstellungen.find(
    (p) => p.schluessel === 'gebaeude.versatz' && !p.geloescht_am,
  );
  const versatz = (versatzzeile?.wert as { lat: number; lng: number } | undefined) ?? {
    lat: 0,
    lng: 0,
  };

  // Die Zeile, die das erste Ziehen anlegt — bis sie aus der Abfrage zurück
  // ist, kommen schon die nächsten Bewegungen.
  const neueVersatzzeile = useRef<Projekteinstellung | null>(null);
  async function verschiebeUmrisse(dLat: number, dLng: number): Promise<void> {
    // Vom Stand in der Datenbank aus rechnen, nicht vom letzten Zeichnen:
    // Kamen mehrere Bewegungen vor dem nächsten Zeichnen, ging jede bis auf
    // die letzte verloren, und die Umrisse hingen dem Finger hinterher.
    const vorhanden = versatzzeile ?? neueVersatzzeile.current;
    if (vorhanden) {
      await speichere('projekteinstellungen', vorhanden, (aktuell: Projekteinstellung) => {
        const alt = (aktuell.wert ?? { lat: 0, lng: 0 }) as { lat: number; lng: number };
        return { wert: { lat: alt.lat + dLat, lng: alt.lng + dLng } };
      });
      return;
    }
    const neu = { lat: versatz.lat + dLat, lng: versatz.lng + dLng };
    const zeile: Projekteinstellung = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: projektId,
      schluessel: 'gebaeude.versatz',
      wert: neu,
    };
    neueVersatzzeile.current = zeile;
    await speichere('projekteinstellungen', zeile, {
      projekt_id: zeile.projekt_id,
      schluessel: zeile.schluessel,
      wert: zeile.wert,
    });
  }

  /** Die Umrisse, wie sie auf dem Luftbild liegen sollen. */
  const ausgerichteteUmrisse = useMemo(
    () =>
      gebaeude.map((g) =>
        g.umriss.map((p) => ({ lat: p.lat + versatz.lat, lng: p.lng + versatz.lng })),
      ),
    [gebaeude, versatz.lat, versatz.lng],
  );
  const [gemessen, setGemessen] = useState<number | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [reiter, setReiter] = useState<Reiter>('dach');
  const seitenleiste = useRef<HTMLElement>(null);
  // Ein Projekt ohne Standort beginnt beim Objekt — ohne Adresse zeigt die
  // Karte nichts, und es gibt kein Dach zu zeichnen.
  const [reiterGesetzt, setReiterGesetzt] = useState(false);
  useEffect(() => {
    if (reiterGesetzt || !projekt) return;
    setReiterGesetzt(true);
    if (startReiter) setReiter(startReiter);
    else if (projekt.lat === null || projekt.lat === undefined) setReiter('objekt');
  }, [projekt, reiterGesetzt]);
  // Verstringt wird nur im Reiter Elektrik. Blieb der Modus beim Wechsel
  // stehen, hängte ein Tipp auf ein Modul im Reiter „Dach“ es still in den
  // gewählten String — und die Karte ließ sich nicht mehr verschieben.
  useEffect(() => {
    if (reiter !== 'anlage' && modus === 'verstringen') {
      setModus('ansehen');
      setAktiverStringId(null);
    }
  }, [reiter, modus]);
  const [stringplanOffen, setStringplanOffen] = useState(false);
  const [dokumentationOffen, setDokumentationOffen] = useState(false);
  const [anlagenblattOffen, setAnlagenblattOffen] = useState(false);
  const [druckAngebot, setDruckAngebot] = useState<Angebotszeile | null>(null);
  const [schattenzeit, setSchattenzeit] = useState<{
    monat: number;
    tag: number;
    stunde: number;
  } | null>(null);
  // Gearbeitet wird auf dem Orthofoto — die erste Quelle. Der Umschalter ist
  // aus der Leiste raus: Die Grauversion war nie mehr als Orientierung, und ein
  // Knopf, den niemand braucht, ist eine Stelle zum Danebengreifen.
  const quellenschluessel = quellen[0]?.schluessel ?? '';

  const quelle = quellen.find((q) => q.schluessel === quellenschluessel) ?? quellen[0]!;
  const aktive = flaechen.find((f) => f.id === aktiveId) ?? null;
  useEffect(() => setGewaehltesHindernis(null), [aktiveId]);
  // Fest, solange sich nichts aendert — die Karte schreibt sonst bei jedem
  // Zeichnen alle Ebenen neu.
  const traegerAufKarte = useMemo(
    () => flaechen.flatMap((f) => traegerFuer(f)?.linien ?? []),
    [flaechen],
  );
  const hindernisAufKarte = useMemo(
    () =>
      aktiveId && gewaehltesHindernis !== null ? { flaecheId: aktiveId, index: gewaehltesHindernis } : null,
    [aktiveId, gewaehltesHindernis],
  );
  const aktiveBelegung = aktive
    ? (belegungen.find((b) => b.flaeche_id === aktive.id && !b.geloescht_am) ?? null)
    : null;
  const modulfelderAufKarte = useMemo(() => {
    const vorhanden = new Set(flaechen.map((f) => f.id));
    return belegungen
      .filter((b) => !b.geloescht_am && vorhanden.has(b.flaeche_id))
      .flatMap((b) => modulfelderVon(b).map((ring) => ({ flaecheId: b.flaeche_id, ring })));
  }, [belegungen, flaechen]);

  /**
   * Alle einstellbaren Werte werden hier aufgeloest, nicht von aussen
   * hereingereicht: Projekteinstellung vor globaler Einstellung vor Vorgabe.
   * Damit wirkt eine Ueberschreibung sofort und ueberall in diesem Projekt.
   */
  const einstellungen: Einstellungsquelle = useMemo(
    () => ({ global: globaleEinstellungen, jeProjekt: projekteinstellungen }),
    [globaleEinstellungen, projekteinstellungen],
  );
  const standardregeln = loeseAuf(
    einstellungen,
    'raster.standard',
    VORGABEN['raster.standard'],
  ).wert;
  const erinnerungsvorgabe = loeseAuf(
    einstellungen,
    'abwicklung.erinnerung',
    VORGABEN['abwicklung.erinnerung'],
  ).wert;
  const pruefvorlage = loeseAuf(
    einstellungen,
    'pruefbefund.vorlage',
    VORGABEN['pruefbefund.vorlage'],
  ).wert;
  const kalkulation = loeseAuf(
    einstellungen,
    'kalkulation.standard',
    VORGABEN['kalkulation.standard'],
  ).wert as Kalkulationsregeln;
  const standardpreise = loeseAuf(
    einstellungen,
    'wirtschaftlichkeit.preise',
    VORGABEN['wirtschaftlichkeit.preise'],
  ).wert as Partial<Preisannahmen>;
  const auslegung = loeseAuf(einstellungen, 'auslegung.temperatur', VORGABEN['auslegung.temperatur']).wert;
  const anlagenwerte = loeseAuf(
    einstellungen,
    'wirtschaftlichkeit.anlage',
    VORGABEN['wirtschaftlichkeit.anlage'],
  ).wert;
  const angebotsvorgaben = loeseAuf(
    einstellungen,
    'angebot.vorgaben',
    VORGABEN['angebot.vorgaben'],
  ).wert;
  const errichter = loeseAuf(einstellungen, 'errichter.angaben', {
    firma: null,
    strasse: null,
    plz: null,
    ort: null,
    telefon: null,
    konzession: null,
    ausgefuehrtNach: 'OVE Richtlinie R11',
  }).wert;
  const netzvorgaben = loeseAuf(einstellungen, 'netzeinreichung.vorgaben', {}).wert as {
    netzbetreiber?: string;
    portal?: string;
    hinweis?: string;
  };
  const preisueberschreibungen = loeseAuf(
    einstellungen,
    'preise.ueberschreibungen',
    VORGABEN['preise.ueberschreibungen'],
  ).wert as Record<string, number>;
  const stringleitungsvorgabe = loeseAuf(
    einstellungen,
    'material.stringleitung',
    VORGABEN['material.stringleitung'],
  ).wert;
  const leitungsartikel = loeseAuf(
    einstellungen,
    'material.leitungsartikel',
    VORGABEN['material.leitungsartikel'],
  ).wert;

  useEffect(() => {
    if (!aktiveId && flaechen.length > 0) setAktiveId(flaechen[0]!.id);
  }, [flaechen, aktiveId]);

  // Jede Aenderung an Flaeche oder Regeln laesst das Raster neu rechnen. Damit
  // kann keine Modulanzahl stehen bleiben, die nicht mehr zur Flaeche passt
  // (PV-E03).
  const planungen = useMemo(() => {
    const ergebnis = new Map<string, Planung>();
    for (const flaeche of flaechen) {
      const belegung = belegungen.find((b) => b.flaeche_id === flaeche.id && !b.geloescht_am);
      const modul = belegung?.modul_id ? module.find((m) => m.id === belegung.modul_id) : undefined;
      const planung = rechne(flaeche, belegung, modul);
      if (planung) ergebnis.set(flaeche.id, planung);
    }
    return ergebnis;
  }, [flaechen, belegungen, module]);

  const gesamt = useMemo(() => summiere([...planungen.values()]), [planungen]);

  // Genauso die Anlage: Stringpruefung und Materialliste werden gerechnet, nie
  // gespeichert. Nur die von Hand ergaenzten Zeilen liegen in der Datenbank.
  const anlage = useMemo(
    () =>
      rechneAnlage({
        flaechen,
        planungen,
        belegungen,
        module,
        strings,
        projektkomponenten,
        komponenten,
        artikel,
        materialsaetze,
        leitungswege,
        materialpositionen,
        kalkulation,
        preisueberschreibungen,
        stringleitung: stringleitungsvorgabe,
        leitungsartikel,
        auslegung,
      }),
    [
      flaechen,
      planungen,
      belegungen,
      module,
      strings,
      projektkomponenten,
      komponenten,
      artikel,
      materialsaetze,
      leitungswege,
      materialpositionen,
      kalkulation,
      preisueberschreibungen,
      stringleitungsvorgabe,
      leitungsartikel,
      auslegung,
    ],
  );

  const farbenJeModul = useMemo(() => modulfarben(strings), [strings]);

  // Stufe 3: Ertrag und Wirtschaftlichkeit. Auch das wird gerechnet, nie
  // gespeichert — eingefroren wird es genau einmal, im Angebot.
  /**
   * Was die Verschattung den Ertrag kostet, je Flaeche (PV-N03, PV-K03).
   *
   * Bis hierher waren die Verschattungsobjekte reine Zeichnung: Sie standen auf
   * der Karte, der Schattenwurf liess sich anzeigen — in der Ertragsrechnung
   * kamen sie nie an. Der Kern konnte es laengst und hatte Tests dafuer, es
   * rief nur niemand auf. Ein Baum vor der Suedseite hat damit null Wirkung auf
   * die Wirtschaftlichkeit gehabt, und das ist die Zahl, die im Angebot steht.
   *
   * Eigener useMemo und nicht in rechneErtrag: Der Lauf geht ueber 73 Tage mal
   * 68 Tagesschritte mal alle Module und dauert auf einem Tablet Sekunden. Er
   * darf nur laufen, wenn sich Objekte oder Belegung wirklich geaendert haben —
   * nicht bei jedem Tastendruck im Kundennamen.
   */
  const verschattungsabschlaege = useMemo(() => {
    const leer = new Map<string, number>();
    const offene = verschattungsobjekte.filter(
      (o) => !o.geloescht_am && o.grundflaeche.length >= 3,
    );
    if (offene.length === 0 || projekt?.lat == null) return leer;
    const bezug = { lat: projekt.lat, lng: projekt.lng ?? 15.24 };
    const objekte: Verschattungsobjekt[] = offene.map((o) => ({
      id: o.id,
      name: o.name,
      art: o.art,
      grundflaeche: ringNachGrundriss(o.grundflaeche, bezug),
      hoeheM: Number(o.hoehe_m),
      ...(o.unterkante_m != null ? { unterkanteM: Number(o.unterkante_m) } : {}),
    }));

    const abschlaege = new Map<string, number>();
    for (const flaeche of flaechen) {
      const planung = planungen.get(flaeche.id);
      if (!planung || planung.gesetzt.length === 0) continue;
      // Modulmitten im selben Grundriss wie die Objekte — sonst rechnet der
      // Schatten gegen Punkte, die woanders liegen.
      // Wie hoch liegt das Modul? Traufhöhe plus der Anstieg bis zur
      // Modulmitte. Ohne Traufhöhe wird auf den Boden geschattet — das
      // überschätzt den Schatten, aber es unterschätzt ihn nie.
      const traufhoehe = flaeche.art === 'dach' && flaeche.hoehe_ueber_grund_m != null ? Number(flaeche.hoehe_ueber_grund_m) : null;
      const vTraufe = Math.min(...planung.kennzahlen.dachebene.map((p) => p.y));
      const steigung = Math.sin((planung.kennzahlen.neigungGrad * Math.PI) / 180);
      const module = planung.gesetzt.map((platz) => ({
        schluessel: `${platz.reihe}:${platz.spalte}`,
        ...(traufhoehe !== null ? { hoeheM: traufhoehe + Math.max(0, platz.mitte.y - vTraufe) * steigung } : {}),
        mitte: nachGrundriss(
          nachGeo(
            nachGrundrissebene(platz.mitte, {
              azimutGrad: planung.kennzahlen.azimutGrad,
              neigungGrad: planung.kennzahlen.neigungGrad,
            }),
            planung.bezug,
          ),
          bezug,
        ),
      }));
      const ergebnis = verschattungsstunden({ module, objekte, breiteGrad: bezug.lat });
      abschlaege.set(
        flaeche.id,
        abschlagAusVerschattung(
          ergebnis,
          module.length,
          bezug.lat,
          planung.kennzahlen.neigungGrad,
          planung.kennzahlen.azimutGrad,
        ),
      );
    }
    return abschlaege;
  }, [verschattungsobjekte, flaechen, planungen, projekt?.lat, projekt?.lng]);

  const ertrag = useMemo(
    () =>
      rechneErtrag({
        flaechen,
        planungen,
        daten: wirtschaftlichkeitsdaten ?? null,
        standort: { lat: projekt?.lat ?? null, lng: projekt?.lng ?? null },
        verschattungsabschlaege,
        standardpreise,
        anlagengueteProzent: anlagenwerte.anlagengueteProzent,
        speicherpreisJeKwh: anlagenwerte.speicherpreisJeKwh,
        speichergroessen: [...anlagenwerte.speichergroessen],
        investitionEuro: anlage.material.summeVerkauf,
        positionenOhnePreis: anlage.material.ohnePreis.length,
      }),
    [
      flaechen,
      planungen,
      wirtschaftlichkeitsdaten,
      projekt?.lat,
      projekt?.lng,
      verschattungsabschlaege,
      standardpreise,
      anlagenwerte,
      anlage.material.summeVerkauf,
      anlage.material.ohnePreis.length,
    ],
  );

  /** Verschattungsobjekte auf der Karte, und ihr Schatten zum gewaehlten Zeitpunkt. */
  const schattenwuerfe = useMemo(() => {
    if (!schattenzeit || verschattungsobjekte.length === 0 || projekt?.lat == null) return [];
    const bezug = { lat: projekt.lat, lng: projekt.lng ?? 15.24 };
    const objekte: Verschattungsobjekt[] = verschattungsobjekte
      .filter((o) => !o.geloescht_am && o.grundflaeche.length >= 3)
      .map((o) => ({
        id: o.id,
        name: o.name,
        art: o.art,
        grundflaeche: ringNachGrundriss(o.grundflaeche, bezug),
        hoeheM: Number(o.hoehe_m),
        ...(o.unterkante_m != null ? { unterkanteM: Number(o.unterkante_m) } : {}),
      }));
    const { schatten } = schattenZuZeitpunkt(
      objekte,
      bezug.lat,
      schattenzeit.monat,
      schattenzeit.tag,
      schattenzeit.stunde,
    );
    return schatten.map((ring) => ringNachGeo(ring, bezug));
  }, [schattenzeit, verschattungsobjekte, projekt?.lat, projekt?.lng]);

  /** Die Linien von Modul zu Modul, umgerechnet auf die Karte. */
  const stringlinien = useMemo(() => {
    return strings
      .filter((s) => !s.geloescht_am)
      .map((string) => ({
        farbe: string.farbe,
        punkte: string.module
          .map((modul) => {
            const planung = planungen.get(modul.flaecheId);
            const platz = planung?.gesetzt.find(
              (p) => p.reihe === modul.reihe && p.spalte === modul.spalte,
            );
            if (!planung || !platz) return undefined;
            const flaeche = flaechen.find((f) => f.id === modul.flaecheId);
            if (flaeche?.art === 'fassade') return undefined;
            return nachGeo(
              nachGrundrissebene(platz.mitte, {
                azimutGrad: planung.kennzahlen.azimutGrad,
                neigungGrad: planung.kennzahlen.neigungGrad,
              }),
              planung.bezug,
            );
          })
          .filter((p): p is Geo => p !== undefined),
      }));
  }, [strings, planungen, flaechen]);

  const mittelpunkt: Geo =
    projekt?.lat !== null && projekt?.lat !== undefined && projekt.lng !== null
      ? { lat: projekt.lat, lng: projekt.lng }
      : { lat: 48.4128, lng: 15.2361 };

  // --- Flaechen anlegen ----------------------------------------------------
  async function legeFlaecheAn(
    felder: Partial<Flaeche> & { polygon: Geo[] },
    nummer: number,
  ): Promise<Flaeche> {
    const flaeche: Flaeche = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: projektId,
      name: `Fläche ${nummer}`,
      art: 'dach',
      aussparungen: [],
      traufkante_index: null,
      azimut_berechnet: null,
      azimut_ueberschrieben: null,
      neigung_grad: 30,
      aufstaenderung_grad: null,
      sonnenstand_grad: null,
      dachtyp: null,
      hoehe_ueber_grund_m: null,
      referenzmass: null,
      fassade: null,
      ...felder,
    };
    await speichere('flaechen', flaeche, {
      projekt_id: flaeche.projekt_id,
      name: flaeche.name,
      art: flaeche.art,
      polygon: flaeche.polygon,
      aussparungen: flaeche.aussparungen,
      traufkante_index: flaeche.traufkante_index,
      azimut_berechnet: flaeche.azimut_berechnet,
      neigung_grad: flaeche.neigung_grad,
      fassade: flaeche.fassade,
    });
    await setzeStandortFallsNoetig(flaeche.polygon.length >= 2 ? flaeche.polygon : entwurf);
    return flaeche;
  }

  async function setzeStandortFallsNoetig(punkte: Geo[]): Promise<void> {
    if (!projekt || punkte.length === 0) return;
    if (projekt.lat !== null && projekt.lng !== null) return;
    const mitte = bezugspunkt(punkte);
    await speichere('projekte', projekt, { lat: mitte.lat, lng: mitte.lng });
  }

  /** Ein neuer Punkt macht das Vorwaertsgehen gegenstandslos. */
  function setzeEntwurf(punkte: Geo[]): void {
    setEntwurf(punkte);
    setVorwaerts([]);
  }

  function entwurfZurueck(): void {
    if (entwurf.length === 0) return;
    setVorwaerts([...vorwaerts, entwurf[entwurf.length - 1]!]);
    setEntwurf(entwurf.slice(0, -1));
  }

  function entwurfVor(): void {
    if (vorwaerts.length === 0) return;
    setEntwurf([...entwurf, vorwaerts[vorwaerts.length - 1]!]);
    setVorwaerts(vorwaerts.slice(0, -1));
  }

  // --- Flaechen und Verschattungsobjekte nachtraeglich formen --------------
  function beginneBearbeitung(
    art: 'flaeche' | 'verschattung' | 'hindernis' | 'modulfeld',
    id: string,
    ring: Geo[],
    index?: number,
  ): void {
    setBearbeitung({ art, id, index, ring, verlauf: [], vorwaerts: [] });
    setGewaehlteEcke(null);
    setModus('bearbeiten');
  }

  /**
   * Jede Aenderung am Ring kommt mit ihrem Vorzustand in den Verlauf — ein
   * Zug mit dem Finger als eine Aenderung, nicht jedes Bewegungsereignis.
   */
  function aendereRing(ring: Geo[], laufend = false): void {
    setBearbeitung((b) =>
      !b
        ? b
        : laufend
          ? { ...b, ring }
          : { ...b, ring, verlauf: [...b.verlauf, b.ring].slice(-50), vorwaerts: [] },
    );
  }

  function ringZurueck(): void {
    setBearbeitung((b) => {
      if (!b || b.verlauf.length === 0) return b;
      return {
        ...b,
        ring: b.verlauf[b.verlauf.length - 1]!,
        verlauf: b.verlauf.slice(0, -1),
        vorwaerts: [...b.vorwaerts, b.ring],
      };
    });
    setGewaehlteEcke(null);
  }

  function ringVor(): void {
    setBearbeitung((b) => {
      if (!b || b.vorwaerts.length === 0) return b;
      return {
        ...b,
        ring: b.vorwaerts[b.vorwaerts.length - 1]!,
        verlauf: [...b.verlauf, b.ring],
        vorwaerts: b.vorwaerts.slice(0, -1),
      };
    });
    setGewaehlteEcke(null);
  }

  function entferneEcke(): void {
    if (!bearbeitung || gewaehlteEcke === null) return;
    // Unter drei Ecken ist es keine Flaeche mehr.
    if (bearbeitung.ring.length <= 3) {
      setMeldung('Drei Ecken braucht eine Fläche mindestens.');
      return;
    }
    aendereRing(bearbeitung.ring.filter((_, i) => i !== gewaehlteEcke));
    setGewaehlteEcke(null);
  }

  async function uebernimmBearbeitung(): Promise<void> {
    if (!bearbeitung) return;
    const mindestflaeche =
      bearbeitung.art === 'flaeche' || bearbeitung.art === 'modulfeld' ? undefined : MINDESTFLAECHE_HINDERNIS_M2;
    if (!ringIstBrauchbar(bearbeitung.ring, mindestflaeche)) {
      setMeldung(
        'So wird das keine Fläche — zwei Ecken fallen zusammen oder alle liegen auf einer Linie.',
      );
      return;
    }
    if (bearbeitung.art === 'flaeche') {
      const flaeche = flaechen.find((f) => f.id === bearbeitung.id);
      if (flaeche) {
        // Traufe und Referenzmaß hängen an Kantenindizes. Eine eingefügte oder
        // entfernte Ecke verschiebt sie — also mitziehen, und den Azimut aus
        // der mitgezogenen Traufe neu rechnen.
        const ring = bearbeitung.ring;
        const traufe =
          flaeche.traufkante_index !== null ? ordneKanteZu(flaeche.polygon, ring, flaeche.traufkante_index) : null;
        const bezug = bezugspunkt(ring);
        await speichere('flaechen', flaeche, {
          polygon: ring,
          ...(traufe !== null
            ? {
                traufkante_index: traufe,
                azimut_berechnet: Number(azimutAusTraufkante(ring.map((p) => nachGrundriss(p, bezug)), traufe).toFixed(2)),
              }
            : {}),
          ...(flaeche.referenzmass
            ? {
                referenzmass: {
                  ...flaeche.referenzmass,
                  kanteIndex: ordneKanteZu(flaeche.polygon, ring, flaeche.referenzmass.kanteIndex),
                },
              }
            : {}),
        });
      }
    } else if (bearbeitung.art === 'modulfeld') {
      const belegung = belegungen.find((b) => b.flaeche_id === bearbeitung.id && !b.geloescht_am);
      if (belegung && bearbeitung.index !== undefined) {
        await speichere('belegungen', belegung, {
          modulfeld: modulfelderVon(belegung).map((alt, i) => (i === bearbeitung.index ? bearbeitung.ring : alt)),
        });
      }
    } else if (bearbeitung.art === 'hindernis') {
      const flaeche = flaechen.find((f) => f.id === bearbeitung.id);
      if (flaeche && bearbeitung.index !== undefined)
        await speichere('flaechen', flaeche, {
          aussparungen: flaeche.aussparungen.map((alt, i) =>
            i === bearbeitung.index ? bearbeitung.ring : alt,
          ),
        });
    } else {
      const objekt = verschattungsobjekte.find((o) => o.id === bearbeitung.id);
      if (objekt)
        await speichere('verschattungsobjekte', objekt, { grundflaeche: bearbeitung.ring });
    }
    setBearbeitung(null);
    setGewaehlteEcke(null);
    setModus('ansehen');
    setMeldung('Übernommen.');
  }

  function brichBearbeitungAb(): void {
    setBearbeitung(null);
    setGewaehlteEcke(null);
    setModus('ansehen');
  }

  function bearbeiteKartenmarke(id: string): void {
    const marke = kartenmarken.find((k) => k.id === id);
    if (!marke) return;
    setMarkeInArbeit({ id: marke.id, art: marke.art, text: marke.text ?? '' });
    setEntwurf(marke.punkte);
    setVorwaerts([]);
    setModus(marke.art === 'bemassung' ? 'bemassung' : 'markierung');
  }

  async function entferneKartenmarke(): Promise<void> {
    const marke = kartenmarken.find((k) => k.id === markeInArbeit?.id);
    if (marke) await speichere('kartenmarken', marke, { geloescht_am: new Date().toISOString() });
    setMarkeInArbeit(null);
    setEntwurf([]);
    setVorwaerts([]);
    setModus('ansehen');
  }

  async function schliesseEntwurfAb(): Promise<void> {
    if ((modus === 'bemassung' || modus === 'markierung') && markeInArbeit) {
      if (entwurf.length !== 2) return;
      const text = markeInArbeit.text.trim() || null;
      const bestehend = kartenmarken.find((k) => k.id === markeInArbeit.id);
      if (bestehend) {
        await speichere('kartenmarken', bestehend, { art: markeInArbeit.art, punkte: entwurf, text });
      } else {
        const marke: Kartenmarke = {
          id: neueId(),
          projekt_id: projektId,
          art: markeInArbeit.art,
          punkte: entwurf,
          text,
          seq: null,
        };
        await speichere('kartenmarken', marke, {
          projekt_id: marke.projekt_id,
          art: marke.art,
          punkte: marke.punkte,
          text: marke.text,
        });
      }
      setMarkeInArbeit(null);
      setEntwurf([]);
      setVorwaerts([]);
      setModus('ansehen');
      return;
    }

    if (modus === 'fassade') {
      if (entwurf.length !== 2) return;
      const flaeche = await legeFlaecheAn(
        {
          polygon: entwurf,
          art: 'fassade',
          name: `Fassade ${flaechen.filter((f) => f.art === 'fassade').length + 1}`,
          neigung_grad: 90,
          fassade: { a: entwurf[0]!, b: entwurf[1]!, hoeheM: 6, seite: 'rechts' },
        },
        flaechen.length + 1,
      );
      setEntwurf([]);
      setModus('ansehen');
      setAktiveId(flaeche.id);
      setMeldung('Fassade angelegt. Höhe und Seite bitte rechts eintragen.');
      return;
    }

    if (modus === 'verschattung-zeichnen') {
      if (entwurf.length < 3) return;
      const objekt: Verschattungsobjektzeile = {
        id: neueId(),
        seq: null,
        schmutzig: true,
        geaendert_am: new Date().toISOString(),
        geloescht_am: null,
        projekt_id: projektId,
        name: `Objekt ${verschattungsobjekte.filter((o) => !o.geloescht_am).length + 1}`,
        art: 'baum',
        grundflaeche: entwurf,
        hoehe_m: 10,
        unterkante_m: null,
      };
      await speichere('verschattungsobjekte', objekt, {
        projekt_id: objekt.projekt_id,
        name: objekt.name,
        art: objekt.art,
        grundflaeche: objekt.grundflaeche,
        hoehe_m: objekt.hoehe_m,
      });
      setEntwurf([]);
      setVorwaerts([]);
      // Zurueck in den Arbeitsbereich, nicht ins Nichts — dort steht die Liste,
      // in der jetzt die Hoehe eingetragen wird.
      setModus('ansehen');
      setReiter('ertrag');
      setMeldung('Verschattungsobjekt angelegt. Höhe bitte rechts eintragen.');
      return;
    }

    if (modus === 'modulfeld') {
      if (!aktive || entwurf.length < 4 || !ringIstBrauchbar(entwurf)) return;
      const regeln = aktiveBelegung?.regeln ?? standardregeln;
      // Im Feld sitzen die Module mittig — wer ein Feld zeichnet, hat die Lage
      // damit schon gewählt. Ein alter Versatz gehörte zur ganzen Fläche.
      const bisher = modulfelderVon(aktiveBelegung);
      // Das erste Feld setzt die Lage auf mittig — wer ein Feld zeichnet, hat
      // sie damit gewählt, und ein alter Versatz gehörte zur ganzen Fläche.
      // Ein weiteres Feld lässt die Regeln, wie sie sind.
      await aendereBelegung({
        modulfeld: [...bisher, entwurf],
        ...(bisher.length === 0
          ? { regeln: { ...regeln, feldlage: { quer: 'mitte', laengs: 'mitte' }, versatzU: 0, versatzV: 0 } }
          : {}),
      });
      setEntwurf([]);
      setVorwaerts([]);
      setModus('ansehen');
      setReiter('module');
      setMeldung(
        bisher.length === 0
          ? 'Modulfeld angelegt. Die Module sitzen mittig darin.'
          : `Modulfeld ${bisher.length + 1} angelegt.`,
      );
      return;
    }

    if (modus === 'aussparung') {
      if (!aktive || !ringIstBrauchbar(entwurf, MINDESTFLAECHE_HINDERNIS_M2)) return;
      await speichere('flaechen', aktive, { aussparungen: [...aktive.aussparungen, entwurf] });
      setEntwurf([]);
      setVorwaerts([]);
      setModus('ansehen');
      // Masse und Abstaende stehen in der Dachaufnahme — dorthin, und das neue
      // gleich gewaehlt, damit man sieht, welches gemeint ist.
      setReiter('dach');
      setGewaehltesHindernis(aktive.aussparungen.length);
      setMeldung('Hindernis angelegt. Maß und Abstände bitte rechts eintragen.');
      return;
    }

    if (!ringIstBrauchbar(entwurf)) return;
    const flaeche = await legeFlaecheAn({ polygon: entwurf }, flaechen.length + 1);
    setEntwurf([]);
    setModus('traufkante');
    setAktiveId(flaeche.id);
  }

  /** Klick auf ein Gebaeude: Umriss holen, teilen, als normale Flaechen anlegen. */
  async function uebernehmeVorschlag(punkt: Geo): Promise<void> {
    setMeldung('Gebäude wird ausgewertet …');
    try {
      // Der Klick liegt auf dem Luftbild, die Umrisse liegen im OSM-Bezug —
      // also den Versatz zuruecknehmen, suchen, und das Ergebnis wieder
      // verschieben. Sonst trifft der Klick daneben.
      const imBestand = { lat: punkt.lat - versatz.lat, lng: punkt.lng - versatz.lng };
      const vorschlag = await schlageFlaechenVor(projektId, imBestand, quelle.url, quelle.maxZoom);
      if (!vorschlag) {
        setMeldung(
          gebaeude.length === 0
            ? 'Es liegen keine Gebäudeumrisse vor. In der Projektliste "Vorbereiten" — dafür braucht es einmal Verbindung.'
            : 'An dieser Stelle liegt kein Gebäude. Bitte auf das Dach tippen oder von Hand zeichnen.',
        );
        return;
      }

      let letzte: string | null = null;
      for (let i = 0; i < vorschlag.polygone.length; i++) {
        const polygon = vorschlag.polygone[i]!.map((p) => ({
          lat: p.lat + versatz.lat,
          lng: p.lng + versatz.lng,
        }));
        const traufkante = vorschlag.traufkanten[i] ?? null;
        const bezug = bezugspunkt(polygon);
        const azimut =
          traufkante === null
            ? null
            : Number(
                azimutAusTraufkante(
                  polygon.map((p) => nachGrundriss(p, bezug)),
                  traufkante,
                ).toFixed(2),
              );
        const flaeche = await legeFlaecheAn(
          { polygon, traufkante_index: traufkante, azimut_berechnet: azimut },
          flaechen.length + 1 + i,
        );
        letzte = flaeche.id;
      }
      if (letzte) setAktiveId(letzte);
      setModus('ansehen');
      setMeldung(vorschlag.meldung);
    } catch (fehler) {
      setMeldung(`Vorschlag fehlgeschlagen: ${String(fehler)}`);
    }
  }

  /**
   * Verschattungsobjekte aus den Gelaendedaten vorschlagen (Kapitel N).
   *
   * Was herauskommt, sind normale Objekte — aenderbar und loeschbar wie die von
   * Hand gesetzten. Art und Hoehe sind geschaetzt, das steht auch in der
   * Meldung. Die Daten sind ausserdem eine Momentaufnahme der Befliegung: Ein
   * Baum waechst, ein Nachbargebaeude kommt dazu.
   */
  async function uebernehmeGelaende(): Promise<void> {
    if (projekt?.lat == null || projekt.lng == null) {
      setMeldung('Erst den Standort setzen — ohne ihn gibt es keine Geländedaten.');
      return;
    }
    setMeldung('Geländedaten werden ausgewertet …');
    const vorschlag = await objekteAusGelaende(projektId, { lat: projekt.lat, lng: projekt.lng });

    for (const objekt of vorschlag.objekte) {
      const zeile: Verschattungsobjektzeile = {
        id: neueId(),
        seq: null,
        schmutzig: true,
        geaendert_am: new Date().toISOString(),
        geloescht_am: null,
        projekt_id: projektId,
        name: `${objekt.name} (Gelände)`,
        art: objekt.art,
        grundflaeche: objekt.grundflaecheGeo,
        hoehe_m: objekt.hoeheM,
        unterkante_m: null,
      };
      await speichere('verschattungsobjekte', zeile, {
        projekt_id: zeile.projekt_id,
        name: zeile.name,
        art: zeile.art,
        grundflaeche: zeile.grundflaeche,
        hoehe_m: zeile.hoehe_m,
      });
    }
    setMeldung(vorschlag.meldung);
  }

  // --- Aendern -------------------------------------------------------------
  async function setzeTraufkante(index: number): Promise<void> {
    if (!aktive) return;
    const bezug = bezugspunkt(aktive.polygon);
    const azimut = azimutAusTraufkante(
      aktive.polygon.map((p) => nachGrundriss(p, bezug)),
      index,
    );
    // Wer eine Traufkante waehlt, legt damit die Ausrichtung fest. Ein vorher
    // von Hand gesetzter Azimut haette sonst weiter gegolten, und die neue
    // Traufe aenderte am Ertrag nichts — genau so beim ersten Test bei Wania
    // aufgefallen (Azimut auf 15 Grad ueberschrieben, Traufe gewechselt,
    // Ertrag gleich).
    const warUeberschrieben = aktive.azimut_ueberschrieben !== null;
    await speichere('flaechen', aktive, {
      traufkante_index: index,
      azimut_berechnet: Number(azimut.toFixed(2)),
      azimut_ueberschrieben: null,
    });
    if (warUeberschrieben) {
      setMeldung(
        `Der von Hand gesetzte Azimut gilt nicht mehr — die Ausrichtung kommt jetzt aus der Traufe: ${azimut.toFixed(0)}°.`,
      );
    }
    setModus('ansehen');
  }

  async function aendereFlaeche(felder: Partial<Flaeche>): Promise<void> {
    if (!aktive) return;
    await speichere('flaechen', aktive, felder as Record<string, unknown>);
  }

  async function aendereBelegung(felder: Partial<Belegung>): Promise<void> {
    if (!aktive) return;
    const vorhanden = belegungen.find((b) => b.flaeche_id === aktive.id && !b.geloescht_am);
    if (vorhanden) {
      await speichere('belegungen', vorhanden, felder as Record<string, unknown>);
      return;
    }
    const neu: Belegung = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      flaeche_id: aktive.id,
      modul_id: null,
      ausrichtung: 'hoch',
      regeln: standardregeln,
      entfernte_plaetze: [],
      zusatzmodule: [],
      ...felder,
    };
    await speichere('belegungen', neu, {
      flaeche_id: neu.flaeche_id,
      modul_id: neu.modul_id,
      ausrichtung: neu.ausrichtung,
      regeln: neu.regeln,
      entfernte_plaetze: neu.entfernte_plaetze,
      zusatzmodule: neu.zusatzmodule,
    });
  }

  /**
   * Ein Modul wurde angetippt. Beim Verstringen heisst das: in den aktiven
   * String hinein oder wieder heraus. Sonst: Modul entfernen oder setzen.
   */
  async function tippeModulAn(flaecheId: string, schluessel: string): Promise<void> {
    const [reihe, spalte] = schluessel.split(':').map(Number);
    if (reihe === undefined || spalte === undefined) return;

    if (modus === 'verstringen') {
      const string = strings.find((s) => s.id === aktiverStringId && !s.geloescht_am);
      if (!string) {
        setMeldung('Erst einen String anlegen, dann Module antippen.');
        return;
      }
      const gleich = (m: { flaecheId: string; reihe: number; spalte: number }): boolean =>
        m.flaecheId === flaecheId && m.reihe === reihe && m.spalte === spalte;
      // Die Listen werden aus dem Stand in der Datenbank gerechnet, nicht aus
      // dem letzten Zeichnen — zwei schnelle Tipps hatten sonst denselben
      // Ausgangsstand, und das erste Modul fiel wieder heraus.
      if (string.module.some(gleich)) {
        await speichere('strings', string, (aktuell: Stringzeile) => ({
          module: aktuell.module.filter((m) => !gleich(m)),
        }));
        return;
      }
      // Ein Tipp ist Absicht: Liegt das Modul schon in einem anderen String,
      // wird es umgehängt. Vorher stand es danach in beiden — elektrisch
      // unmöglich, und die Spannungsprüfung zählte es doppelt. Beim Wischen
      // wird stattdessen übersprungen, weil ein Strich schnell zu weit geht.
      const anderer = strings.find((s) => s.id !== string.id && !s.geloescht_am && s.module.some(gleich));
      if (anderer) {
        await speichere('strings', anderer, (aktuell: Stringzeile) => ({
          module: aktuell.module.filter((m) => !gleich(m)),
        }));
        setMeldung(`Modul von ${anderer.name} nach ${string.name} umgehängt.`);
      }
      await speichere('strings', string, (aktuell: Stringzeile) => ({
        module: aktuell.module.some(gleich) ? aktuell.module : [...aktuell.module, { flaecheId, reihe, spalte }],
      }));
      return;
    }

    if (modus !== 'module') return;
    const belegung = belegungen.find((b) => b.flaeche_id === flaecheId && !b.geloescht_am);
    if (!belegung) return;
    // Umschalten auf dem Stand in der Datenbank, in derselben Transaktion —
    // sonst rechnete der zweite schnelle Tipp vom Stand vor dem ersten.
    let vorher: string[] = belegung.entfernte_plaetze;
    await speichere('belegungen', belegung, (aktuell: Belegung) => {
      vorher = aktuell.entfernte_plaetze;
      const entfernt = new Set(aktuell.entfernte_plaetze);
      if (entfernt.has(schluessel)) entfernt.delete(schluessel);
      else entfernt.add(schluessel);
      return { entfernte_plaetze: [...entfernt] };
    });
    setModulVerlauf((v) => [...v, { belegungId: belegung.id, plaetze: vorher }]);
    setModulVorwaerts([]);
  }

  /**
   * Ueber Module gewischt (Mathias, 11/2026): alle auf einmal in den aktiven
   * String oder heraus — eine Aenderung, nicht eine je Modul.
   */
  async function wischeModule(gewischt: Array<{ flaecheId: string; schluessel: string }>): Promise<void> {
    const string = strings.find((s) => s.id === aktiverStringId && !s.geloescht_am);
    if (!string) {
      setMeldung('Erst einen String anlegen, dann über die Module wischen.');
      return;
    }
    const zeiger = gewischt.flatMap(({ flaecheId, schluessel }) => {
      const [reihe, spalte] = schluessel.split(':').map(Number);
      return reihe === undefined || spalte === undefined || Number.isNaN(reihe) || Number.isNaN(spalte)
        ? []
        : [{ flaecheId, reihe, spalte }];
    });
    const ergebnis = wische(
      string,
      strings.filter((s) => !s.geloescht_am),
      zeiger,
    );
    if (ergebnis.geaendert > 0) await speichere('strings', string, { module: ergebnis.module });
    const teile = [
      ergebnis.art === 'heraus'
        ? `${ergebnis.geaendert} Modul(e) aus ${string.name} genommen.`
        : `${ergebnis.geaendert} Modul(e) zu ${string.name} dazu, jetzt ${ergebnis.module.length}.`,
    ];
    if (ergebnis.uebersprungen > 0) {
      teile.push(
        `${ergebnis.uebersprungen} gehören schon zu einem anderen String und sind übersprungen — dort erst herausnehmen.`,
      );
    }
    setMeldung(teile.join(' '));
  }

  /**
   * Zurueck und vor beim An- und Abschalten von Modulen. Jeder Schritt merkt
   * sich, wie die entfernten Plaetze vorher aussahen — auch "alle wieder
   * aktivieren" laesst sich so zuruecknehmen.
   */
  const [modulVerlauf, setModulVerlauf] = useState<Array<{ belegungId: string; plaetze: string[] }>>(
    [],
  );
  const [modulVorwaerts, setModulVorwaerts] = useState<
    Array<{ belegungId: string; plaetze: string[] }>
  >([]);

  async function setzeEntfernte(belegung: Belegung, plaetze: string[]): Promise<void> {
    setModulVerlauf((v) => [...v, { belegungId: belegung.id, plaetze: belegung.entfernte_plaetze }]);
    setModulVorwaerts([]);
    await speichere('belegungen', belegung, { entfernte_plaetze: plaetze });
  }

  async function modulSchritt(richtung: 'zurueck' | 'vor'): Promise<void> {
    const stapel = richtung === 'zurueck' ? modulVerlauf : modulVorwaerts;
    const schritt = stapel[stapel.length - 1];
    if (!schritt) return;
    const belegung = belegungen.find((b) => b.id === schritt.belegungId);
    if (!belegung) return;
    const gegenstueck = { belegungId: belegung.id, plaetze: belegung.entfernte_plaetze };
    if (richtung === 'zurueck') {
      setModulVerlauf((v) => v.slice(0, -1));
      setModulVorwaerts((v) => [...v, gegenstueck]);
    } else {
      setModulVorwaerts((v) => v.slice(0, -1));
      setModulVerlauf((v) => [...v, gegenstueck]);
    }
    await speichere('belegungen', belegung, { entfernte_plaetze: schritt.plaetze });
  }

  /**
   * Projekt entfernen (PV-B12 sinngemaess: auch das ist eine ganz normale
   * Aenderung und geht offline).
   *
   * Weich, wie ueberall hier: Die Zeile bleibt stehen, damit Angebotsnummern
   * und Auftraege nicht ins Leere zeigen. Wiederzufinden ist das Projekt in der
   * Liste unter "Entfernt" — ein Fehlgriff darf nicht endgueltig sein.
   */
  async function entferneProjekt(): Promise<void> {
    if (!projekt) return;
    const haengtDran = [
      flaechen.length > 0 ? `${flaechen.length} Fläche(n)` : null,
      angebote.length > 0 ? `${angebote.length} Angebot(e)` : null,
      auftraege.filter((a) => !a.geloescht_am).length > 0 ? 'ein Auftrag' : null,
    ].filter(Boolean);

    const frage =
      `${projekt.kunde_name || 'Dieses Projekt'} entfernen?` +
      (haengtDran.length > 0 ? `\n\nDaran hängen: ${haengtDran.join(', ')}.` : '') +
      '\n\nDas Projekt verschwindet aus der Liste. Rückgängig machen lässt es sich dort unter "Entfernt".';
    if (!confirm(frage)) return;

    await speichere('projekte', projekt, { geloescht_am: new Date().toISOString() });
    onZurueck();
  }

  /**
   * Die Flaeche auf die Traufkante rechtwinklig machen (PV-D21).
   *
   * Von Hand gezeichnete und aus OSM uebernommene Umrisse sind selten exakt
   * rechtwinklig. Ein Grad Schraeglage kostet auf neun Metern schon fuenfzehn
   * Zentimeter — genug, dass die oberste Modulreihe an die Kante stoesst und
   * wegfaellt, obwohl auf dem Bildschirm Platz auszusehen scheint.
   *
   * Ausgerichtet wird auf die Traufkante, nicht auf die vorherrschende
   * Richtung: Das Raster laeuft laengs der Traufe, also muss sich der Rest
   * danach richten.
   */
  async function begradigeFlaeche(): Promise<void> {
    if (!aktive || aktive.polygon.length < 4) return;
    const bezug = bezugspunkt(aktive.polygon);
    const grundriss = aktive.polygon.map((p) => nachGrundriss(p, bezug));
    const index = (aktive.traufkante_index ?? 0) % grundriss.length;
    const a = grundriss[index]!;
    const b = grundriss[(index + 1) % grundriss.length]!;
    const richtung = Math.atan2(b.y - a.y, b.x - a.x);
    const gerade = begradige(grundriss, 15, 3, richtung);
    await speichere('flaechen', aktive, {
      polygon: gerade.map((p) => nachGeo(p, bezug)),
    });
    setMeldung('Auf die Traufkante ausgerichtet. Die Belegung ist neu gerechnet.');
  }

  /**
   * Eine Flaeche entfernen — gleich in der Liste, nicht ganz unten im Panel.
   * Bei fuenf Flaechen und einem langen Panel scrollt man sonst durch das halbe
   * Formular, nur um eine Fehlmessung wegzubekommen.
   */
  async function loescheFlaeche(welche?: Flaeche): Promise<void> {
    const ziel = welche ?? aktive;
    if (!ziel) return;
    if (!confirm(`${ziel.name} entfernen?\n\nDie Belegung dieser Fläche geht mit.`)) return;
    await speichere('flaechen', ziel, { geloescht_am: new Date().toISOString() });
    if (ziel.id === aktiveId) setAktiveId(null);
  }

  /** Die wahre Länge einer Bemaßung auf dem Dach (siehe `dachstrecke`). */
  const dachlaenge = useCallback(
    (a: Geo, b: Geo): number | null => dachstrecke(flaechen, a, b)?.laengeM ?? null,
    [flaechen],
  );

  if (projekt === undefined) return <div className="liste gedaempft">Projekt wird geladen …</div>;
  if (!projekt) return <div className="liste">Projekt nicht gefunden.</div>;

  // Drei Punkte allein genuegen nicht: Fallen zwei zusammen oder liegen alle
  // auf einer Linie, entstuende eine Flaeche ohne Inhalt.
  const genugPunkte =
    modus === 'modulfeld'
      ? entwurf.length >= 4 && ringIstBrauchbar(entwurf)
      : modus === 'fassade' || modus === 'bemassung'
      ? entwurf.length === 2
      : modus === 'markierung'
        ? entwurf.length === 2 && (markeInArbeit?.art !== 'hinweis' || !!markeInArbeit.text.trim())
        : ringIstBrauchbar(
            entwurf,
            modus === 'aussparung' || modus === 'verschattung-zeichnen' ? MINDESTFLAECHE_HINDERNIS_M2 : undefined,
          );

  /**
   * Was noch fehlt, im Klartext. Ein grauer "Fertig"-Knopf ohne Begruendung
   * sagt nicht, ob noch ein Punkt fehlt oder ob etwas nicht stimmt.
   */
  const fehlendZumAbschluss: string | null =
    modus === 'traufkante'
      ? 'Die Kante antippen, an der die Traufe liegt — oder abbrechen.'
      : modus === 'umrisse' || modus === 'module'
      ? null
      : modus === 'bearbeiten'
      ? (bearbeitung?.ring.length ?? 0) >= 3
        ? null
        : 'Eine Fläche braucht mindestens drei Ecken.'
      : modus === 'fassade'
        ? entwurf.length < 2
          ? `Noch ${2 - entwurf.length} Punkt(e) — Anfang und Ende der Wand.`
          : null
        : modus === 'bemassung'
          ? entwurf.length < 2
            ? `Noch ${2 - entwurf.length} Punkt(e) — Anfang und Ende.`
            : null
          : modus === 'markierung'
            ? entwurf.length === 0
              ? 'Erst die Pfeilspitze antippen — dort, wo es sitzt.'
              : entwurf.length === 1
                ? 'Jetzt antippen, wo die Beschriftung stehen soll.'
                : genugPunkte
                  ? null
                  : 'Für einen Hinweis fehlt noch der Text.'
        : genugPunkte
          ? null
          : modus === 'modulfeld' && entwurf.length < 4
            ? `Noch ${4 - entwurf.length} Punkt(e) — ein Modulfeld hat mindestens vier Ecken.`
          : entwurf.length < 3
            ? `Noch ${3 - entwurf.length} Punkt(e).`
            : modus === 'aussparung' || modus === 'verschattung-zeichnen'
              ? 'Zwei Ecken fallen zusammen oder alle liegen auf einer Linie.'
              : 'Zu klein für eine Dachfläche, oder zwei Ecken fallen zusammen.';

  return (
    <div className="inhalt">
      {druckAngebot && (
        <Angebotsdruck
          angebot={druckAngebot}
          flaechen={flaechen}
          planungen={planungen}
          vorgaben={angebotsvorgaben}
          onSchliessen={() => setDruckAngebot(null)}
        />
      )}

      {anlagenblattOffen && projekt && (
        <Anlagendokumentation
          projekt={projekt}
          flaechen={flaechen}
          planungen={planungen}
          strings={strings}
          projektkomponenten={projektkomponenten}
          komponenten={komponenten}
          module={module}
          modulJeFlaeche={
            new Map(
              belegungen
                .filter((b) => !b.geloescht_am && b.modul_id)
                .map((b) => [b.flaeche_id, b.modul_id!] as [string, string]),
            )
          }
          pruefbefund={pruefbefunde.find((p) => !p.geloescht_am) ?? null}
          anlage={anlage}
          pruefvorlage={pruefvorlage}
          errichter={errichter}
          netzvorgaben={netzvorgaben}
          leistungKwp={gesamt.leistungKwp}
          onSchliessen={() => setAnlagenblattOffen(false)}
        />
      )}

      {dokumentationOffen && projekt && (
        <Dokumentation
          projekt={projekt}
          flaechen={flaechen}
          planungen={planungen}
          strings={strings}
          projektkomponenten={projektkomponenten}
          komponenten={komponenten}
          auftrag={auftraege.find((a) => !a.geloescht_am) ?? null}
          pruefbefund={pruefbefunde.find((p) => !p.geloescht_am) ?? null}
          leistungKwp={gesamt.leistungKwp}
          ertragJahr={ertrag.ertrag.jahr}
          onSchliessen={() => setDokumentationOffen(false)}
        />
      )}

      {stringplanOffen && (
        <Stringplan
          flaechen={flaechen}
          planungen={planungen}
          strings={strings}
          onSchliessen={() => setStringplanOffen(false)}
        />
      )}

      <div className="karte">
        <Karte
          mittelpunkt={mittelpunkt}
          zoomAuf={zoomAuf}
          quelle={quelle}
          flaechen={flaechen}
          planungen={planungen}
          aktiveFlaecheId={aktiveId}
          modus={modus}
          gebaeudeumrisse={ausgerichteteUmrisse}
          umrissquelle={gebaeude[0]?.quelle ?? null}
          onUmrisseVerschoben={(dLat, dLng) => void verschiebeUmrisse(dLat, dLng)}
          modulfarben={farbenJeModul}
          stringlinien={stringlinien}
          verschattungsobjekte={verschattungsobjekte
            .filter((o) => !o.geloescht_am)
            .map((o) => o.grundflaeche)}
          schattenwuerfe={schattenwuerfe}
          entwurf={entwurf}
          onEntwurf={setzeEntwurf}
          gewaehltesHindernis={hindernisAufKarte}
          traeger={traegerAufKarte}
          modulfelder={modulfelderAufKarte}
          hervorgehobeneKante={
            aktive && referenzkante !== null && aktive.polygon.length >= 2
              ? [
                  aktive.polygon[referenzkante % aktive.polygon.length]!,
                  aktive.polygon[(referenzkante + 1) % aktive.polygon.length]!,
                ]
              : null
          }
          bearbeitung={modus === 'bearbeiten' ? (bearbeitung?.ring ?? null) : null}
          onBearbeitung={aendereRing}
          gewaehlteEcke={gewaehlteEcke}
          onEckeGewaehlt={setGewaehlteEcke}
          onFlaecheGewaehlt={setAktiveId}
          onKanteGewaehlt={(index) => void setzeTraufkante(index)}
          onModulGetippt={(flaecheId, schluessel) => void tippeModulAn(flaecheId, schluessel)}
          onModuleGewischt={(gewischt) => void wischeModule(gewischt)}
          wischfarbe={strings.find((s) => s.id === aktiverStringId)?.farbe ?? null}
          onGemessen={setGemessen}
          onGebaeudeGetippt={(punkt) => void uebernehmeVorschlag(punkt)}
          kartenmarken={kartenmarken.filter((k) => !k.geloescht_am && k.id !== markeInArbeit?.id)}
          entwurfsmarke={markeInArbeit}
          markenWaehlbar={!nurLesen}
          onKartenmarkeGetippt={bearbeiteKartenmarke}
          dachlaenge={dachlaenge}
        />

        {ARBEITSMODI.includes(modus) ? (
          <Arbeitsmenue
            modus={modus}
            titel={
              modus !== 'bearbeiten'
                ? undefined
                : bearbeitung?.art === 'hindernis'
                  ? 'Hindernis verschieben'
                  : bearbeitung?.art === 'modulfeld'
                    ? 'Modulfeld bearbeiten'
                  : bearbeitung?.art === 'verschattung'
                    ? 'Objekt bearbeiten'
                    : undefined
            }
            anzahl={modus === 'bearbeiten' ? (bearbeitung?.ring.length ?? 0) : entwurf.length}
            genug={
              // Beim Ausrichten gibt es nichts zu pruefen: Der Versatz ist
              // beim Ziehen schon gespeichert, "Fertig" beendet nur den Modus.
              // Vorher war der Knopf dort gesperrt und der Pfeil durchgestrichen.
              modus === 'traufkante'
                ? false
                : modus === 'umrisse' || modus === 'module'
                ? true
                : modus === 'bearbeiten'
                  ? (bearbeitung?.ring.length ?? 0) >= 3
                  : genugPunkte
            }
            fehlend={fehlendZumAbschluss}
            kannZurueck={
              modus === 'module'
                ? modulVerlauf.length > 0
                : modus === 'bearbeiten'
                  ? (bearbeitung?.verlauf.length ?? 0) > 0
                  : entwurf.length > 0
            }
            kannVor={
              modus === 'module'
                ? modulVorwaerts.length > 0
                : modus === 'bearbeiten'
                  ? (bearbeitung?.vorwaerts.length ?? 0) > 0
                  : vorwaerts.length > 0
            }
            entfernteModule={aktiveBelegung?.entfernte_plaetze.length ?? 0}
            onAlleAktivieren={() => {
              if (aktiveBelegung) void setzeEntfernte(aktiveBelegung, []);
            }}
            gewaehlteEcke={gewaehlteEcke}
            zusatz={
              markeInArbeit && (modus === 'bemassung' || modus === 'markierung') ? (
                <>
                  {/* Das Maß auch hier: Liegt die Linie am unteren Rand, steht
                      das Menü genau über ihrem Schild. */}
                  {modus === 'bemassung' && entwurf.length === 2 && (
                    <strong>
                      {(() => {
                        const bild = streckeM(entwurf[0]!, entwurf[1]!);
                        const dach = dachlaenge(entwurf[0]!, entwurf[1]!);
                        const m = (x: number): string => `${x.toFixed(2).replace('.', ',')} m`;
                        return dach !== null
                          ? `${m(dach)} am Dach (${m(bild)} im Luftbild)`
                          : `${m(bild)} im Luftbild`;
                      })()}
                    </strong>
                  )}
                  {modus === 'markierung' &&
                    (['wechselrichter', 'verteiler', 'hinweis'] as const).map((art) => (
                      <button
                        key={art}
                        aria-pressed={markeInArbeit.art === art}
                        onClick={() => setMarkeInArbeit({ ...markeInArbeit, art })}
                      >
                        {MARKENNAMEN[art]}
                      </button>
                    ))}
                  <input
                    value={markeInArbeit.text}
                    placeholder={
                      modus === 'bemassung'
                        ? 'Bezeichnung (frei lassen: nur das Maß)'
                        : markeInArbeit.art === 'hinweis'
                          ? 'Hinweis, z. B. „Leiter nur hier anlegen“'
                          : 'Ort oder Typ, z. B. „Keller, Symo 10“'
                    }
                    onChange={(e) => setMarkeInArbeit({ ...markeInArbeit, text: e.target.value })}
                  />
                  {markeInArbeit.id && (
                    <button className="gefaehrlich" onClick={() => void entferneKartenmarke()}>
                      Entfernen
                    </button>
                  )}
                </>
              ) : undefined
            }
            onZurueck={() =>
              modus === 'module'
                ? void modulSchritt('zurueck')
                : modus === 'bearbeiten'
                  ? ringZurueck()
                  : entwurfZurueck()
            }
            onVor={() =>
              modus === 'module'
                ? void modulSchritt('vor')
                : modus === 'bearbeiten'
                  ? ringVor()
                  : entwurfVor()
            }
            onEckeEntfernen={entferneEcke}
            onFertig={() => {
              if (modus === 'module') {
                setModus('ansehen');
                setModulVerlauf([]);
                setModulVorwaerts([]);
                return;
              }
              if (modus === 'umrisse') {
                setModus('ansehen');
                setMeldung('Versatz gespeichert. Er gilt für alle Gebäude dieses Projekts.');
                return;
              }
              if (modus === 'bearbeiten') void uebernimmBearbeitung();
              else void schliesseEntwurfAb();
            }}
            onAbbrechen={() => {
              if (modus === 'umrisse') setModus('ansehen');
              else if (modus === 'bearbeiten') brichBearbeitungAb();
              else {
                setEntwurf([]);
                setVorwaerts([]);
                setMarkeInArbeit(null);
                setModus('ansehen');
              }
            }}
          />
        ) : (
          <Werkzeugleiste
            nurLesen={nurLesen}
            stand={{
              modus,
              hatFlaechen: flaechen.length > 0,
              aktiveFlaeche: aktive
                ? {
                    istDach: aktive.art !== 'fassade',
                    hatModule: (planungen.get(aktive.id)?.raster.module.length ?? 0) > 0,
                  }
                : null,
            }}
            griffe={{
              setModus: (ziel) => {
                if (ziel === 'bearbeiten') {
                  if (aktive) beginneBearbeitung('flaeche', aktive.id, aktive.polygon);
                  return;
                }
                setModus(ziel);
                setEntwurf([]);
                setVorwaerts([]);
                setMarkeInArbeit(
                  ziel === 'bemassung'
                    ? { id: null, art: 'bemassung', text: '' }
                    : ziel === 'markierung'
                      ? { id: null, art: 'wechselrichter', text: '' }
                      : null,
                );
                if (ziel === 'vorschlag') {
                  setMeldung('Auf das Gebäude tippen — der Umriss wird geteilt.');
                }
                if (ziel === 'messen') {
                  setMeldung(
                    'Anfang und Ende antippen — das Maß steht dann an der Linie. Ein dritter Tipp misst neu.',
                  );
                }
                if (ziel === 'verschattung-zeichnen') setReiter('ertrag');
              },
              onZurueck,
              onGelaende: () => void uebernehmeGelaende(),
            }}
          />
        )}

        {meldung && (
          <div
            className="hinweis"
            role="status"
            style={{
              position: 'absolute',
              // Während einer Arbeit sitzt unten das Arbeitsmenü — eine Meldung
              // wie „Zwei Ecken fallen zusammen“ lag sonst genau dahinter.
              ...(ARBEITSMODI.includes(modus) ? { top: '0.75rem' } : { bottom: '1.5rem' }),
              left: '0.75rem',
              right: '0.75rem',
              zIndex: 4,
            }}
          >
            {meldung}
            <button
              onClick={() => setMeldung(null)}
              className="schlicht"
              style={{ float: 'right' }}
            >
              ×
            </button>
          </div>
        )}
      </div>

      <aside className="seitenleiste" ref={seitenleiste}>
        <p className="marke">{projekt.kunde_name || 'Projekt'}</p>
        {nurLesen && (
          <div className="hinweis">
            Die Planung ist hier zum Ansehen. Ändern können sie Vertrieb, Büro und Admin — Fotos,
            Auftrag und Prüfbefund gehen wie gewohnt.
          </div>
        )}

        <div className="reiter reiter--schritte">
          {SCHRITTE.filter(([wert]) => !nurLesen || (wert !== 'ertrag' && wert !== 'angebot')).map(([wert, text], index) => (
            <button key={wert} aria-pressed={reiter === wert} onClick={() => setReiter(wert)}>
              <span className="reiter__nummer">{index + 1}</span>
              {text}
            </button>
          ))}
        </div>
        <div className="reiter reiter--neben">
          {NEBENREITER.map(([wert, text]) => (
            <button key={wert} aria-pressed={reiter === wert} onClick={() => setReiter(wert)}>
              {text}
            </button>
          ))}
        </div>

        <dl className="zahlenblock">
          <div>
            <dt>Module</dt>
            <dd>{gesamt.anzahl}</dd>
          </div>
          <div>
            <dt>Leistung</dt>
            <dd>{gesamt.leistungKwp.toFixed(2)} kWp</dd>
          </div>
        </dl>

        {reiter === 'fotos' && <Fotos projektId={projektId} flaecheId={aktiveId} />}

        {reiter === 'anlage' && (
<fieldset className="nurlesen" disabled={nurLesen}>
          <Anlagepanel
            projektId={projektId}
            flaechen={flaechen}
            planungen={planungen}
            anlage={anlage}
            strings={strings}
            projektkomponenten={projektkomponenten}
            komponenten={komponenten}
            leitungswege={leitungswege}
            aktiverStringId={aktiverStringId}
            onAktiverString={(id) => {
              setAktiverStringId(id);
              // Ohne String kein Verstringmodus. Seit der Knopf aus der Leiste
              // raus ist, waere das sonst eine Sackgasse.
              setModus(id ? 'verstringen' : 'ansehen');
            }}
            onStringplan={() => setStringplanOffen(true)}
          />
</fieldset>
        )}

        {reiter === 'material' && (
          <fieldset className="nurlesen" disabled={nurLesen}>
            <Materialpanel
              projektId={projektId}
              anlage={anlage}
              materialpositionen={materialpositionen}
              ohnePreise={nurLesen}
            />
          </fieldset>
        )}

        {reiter === 'ertrag' && (
          <>
            <Wirtschaftlichkeitspanel
              projektId={projektId}
              standort={{ lat: projekt.lat, lng: projekt.lng }}
              daten={wirtschaftlichkeitsdaten ?? null}
              ertrag={ertrag}
            />
            <Verschattungsliste
              objekte={verschattungsobjekte.filter((o) => !o.geloescht_am)}
              abschlaege={flaechen.map((f) => ({
                name: f.name,
                prozent: verschattungsabschlaege.get(f.id) ?? null,
              }))}
              zeit={schattenzeit}
              onZeit={setSchattenzeit}
              onZeichnen={() => {
                setModus('verschattung-zeichnen');
                setEntwurf([]);
                setVorwaerts([]);
              }}
              onBearbeiten={(objekt) =>
                beginneBearbeitung('verschattung', objekt.id, objekt.grundflaeche)
              }
              inBearbeitung={bearbeitung?.art === 'verschattung' ? bearbeitung.id : null}
            />
          </>
        )}

        {reiter === 'angebot' && (
          <Angebotspanel
            projekt={projekt}
            flaechen={flaechen}
            belegungen={belegungen}
            strings={strings}
            projektkomponenten={projektkomponenten}
            anlage={anlage}
            ertrag={ertrag}
            angebote={angebote}
            offeneAenderungen={offeneAenderungen}
            online={online}
            onDrucken={setDruckAngebot}
          />
        )}

        {reiter === 'abwicklung' && (
          <Ablaufpanel
            projekt={projekt}
            angebote={angebote}
            auftraege={auftraege}
            schritte={projektschritte}
            dokumente={dokumente}
            vorgabe={erinnerungsvorgabe}
            nurLesen={nurLesen}
          />
        )}
        {reiter === 'abwicklung' && (
          <Abwicklungspanel
            projektId={projektId}
            anlage={anlage}
            strings={strings}
            angebote={angebote}
            auftraege={auftraege}
            pruefbefunde={pruefbefunde}
            vorlagen={vorlagen}
            pruefvorlage={pruefvorlage}
            onStringplan={() => setStringplanOffen(true)}
            onDokumentation={() => setDokumentationOffen(true)}
            onAnlagenblatt={() => setAnlagenblattOffen(true)}
            projekt={projekt}
            nurLesen={nurLesen}
          />
        )}
        {reiter === 'abwicklung' && (
          <Beschreibungsexport
            eingabe={() => ({
              projekt,
              flaechen,
              planungen,
              belegungen,
              strings,
              module,
              projektkomponenten,
              komponenten,
              leitungswege,
              anlage,
              pruefbefund: pruefbefunde.find((p) => !p.geloescht_am) ?? null,
              pruefvorlage,
              errichter,
              leistungKwp: gesamt.leistungKwp,
              stand: new Date(),
            })}
          />
        )}

        {reiter === 'objekt' && (
          <>
            <fieldset className="nurlesen" disabled={nurLesen}>
              <Projektfelder projekt={projekt} quelle={quelle} vorladen={vorladen} />

              {!nurLesen && (
                <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
                  <button className="gefaehrlich" onClick={() => void entferneProjekt()}>
                    Projekt entfernen
                  </button>
                </div>
              )}
            </fieldset>
          </>
        )}

        {reiter === 'dach' && (
          <>
            <h2 style={{ marginTop: '0.5rem' }}>Flächen</h2>
            <div className="knopfreihe">
              {flaechen.map((flaeche) => (
                <span key={flaeche.id} className="flaechenwahl">
                  <button
                    aria-pressed={flaeche.id === aktiveId}
                    onClick={() => setAktiveId(flaeche.id)}
                  >
                    {flaeche.name}
                  </button>
                  {!nurLesen && (
                    <button
                      className="schlicht gefaehrlich"
                      onClick={() => void loescheFlaeche(flaeche)}
                      title={`${flaeche.name} entfernen`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {flaechen.length === 0 && (
                <span style={{ color: 'var(--grau)', fontSize: '0.9rem' }}>
                  Noch keine Fläche. Oben links „Gebäude auswählen“ oder „Fläche zeichnen“.
                </span>
              )}
            </div>

            {aktive?.art === 'fassade' && (
              <>
                <fieldset className="nurlesen" disabled={nurLesen}>
                  <Fassadenfelder flaeche={aktive} onFlaeche={(f) => void aendereFlaeche(f)} />
                </fieldset>
                <fieldset className="nurlesen" disabled={nurLesen}>
                  <Fassadenansicht
                    flaeche={aktive}
                    planung={planungen.get(aktive.id) ?? null}
                    onFlaeche={(f) => void aendereFlaeche(f)}
                    onModulGetippt={(s) => void tippeModulAn(aktive.id, s)}
                  />
                </fieldset>
              </>
            )}

            {aktive && (
              <fieldset className="nurlesen" disabled={nurLesen}>
              <Flaechenpanel
                // Je Flaeche neu aufbauen. Ohne Schluessel blieb beim Wechsel der
                // Name der vorigen Flaeche im Feld stehen (defaultValue) — und
                // wer hineintippte, benannte die neue Flaeche um.
                key={aktive.id}
                teil="dach"
                flaeche={aktive}
                belegung={
                  belegungen.find((b) => b.flaeche_id === aktive.id && !b.geloescht_am) ?? null
                }
                module={module}
                planung={planungen.get(aktive.id) ?? null}
                gemessen={gemessen}
                onFlaeche={(felder) => void aendereFlaeche(felder)}
                onBelegung={(felder) => void aendereBelegung(felder)}
                onTraufkanteWaehlen={() => setModus('traufkante')}
                onLoeschen={() => void loescheFlaeche()}
                onReferenzkante={setReferenzkante}
                onBegradigen={() => void begradigeFlaeche()}
                gewaehltesHindernis={gewaehltesHindernis}
                onHindernisGewaehlt={setGewaehltesHindernis}
                onHindernisFormen={(index) => {
                  const ring = aktive.aussparungen[index];
                  if (!ring) return;
                  setGewaehltesHindernis(index);
                  beginneBearbeitung('hindernis', aktive.id, ring, index);
                  setZoomAuf([...ring]);
                }}
              />
              </fieldset>
            )}
          </>
        )}

        {reiter === 'module' && (
          <>
            <h2 style={{ marginTop: '0.5rem' }}>Flächen</h2>
            <div className="knopfreihe">
              {flaechen.map((flaeche) => (
                <span key={flaeche.id} className="flaechenwahl">
                  <button
                    aria-pressed={flaeche.id === aktiveId}
                    onClick={() => setAktiveId(flaeche.id)}
                  >
                    {flaeche.name}
                  </button>
                  {!nurLesen && (
                    <button
                      className="schlicht gefaehrlich"
                      onClick={() => void loescheFlaeche(flaeche)}
                      title={`${flaeche.name} entfernen`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {flaechen.length === 0 && (
                <span style={{ color: 'var(--grau)', fontSize: '0.9rem' }}>
                  Noch keine Fläche. Oben links „Gebäude auswählen“ oder „Fläche zeichnen“.
                </span>
              )}
            </div>

            {aktive && aktive.art !== 'fassade' && (
              <fieldset className="nurlesen" disabled={nurLesen}>
              <Flaechenpanel
                key={aktive.id}
                teil="module"
                flaeche={aktive}
                belegung={
                  belegungen.find((b) => b.flaeche_id === aktive.id && !b.geloescht_am) ?? null
                }
                module={module}
                planung={planungen.get(aktive.id) ?? null}
                gemessen={gemessen}
                onFlaeche={(felder) => void aendereFlaeche(felder)}
                onBelegung={(felder) => void aendereBelegung(felder)}
                onTraufkanteWaehlen={() => setModus('traufkante')}
                onLoeschen={() => void loescheFlaeche()}
                onReferenzkante={setReferenzkante}
                onBegradigen={() => void begradigeFlaeche()}
                gewaehltesHindernis={gewaehltesHindernis}
                onHindernisGewaehlt={setGewaehltesHindernis}
                onHindernisFormen={(index) => {
                  const ring = aktive.aussparungen[index];
                  if (!ring) return;
                  setGewaehltesHindernis(index);
                  beginneBearbeitung('hindernis', aktive.id, ring, index);
                  setMeldung('Hindernis ziehen oder Ecken verschieben, dann „Fertig“.');
                }}
                onModulfeldZeichnen={() => {
                  setEntwurf([]);
                  setVorwaerts([]);
                  setModus('modulfeld');
                }}
                onModulfeldFormen={(index) => {
                  const ring = modulfelderVon(aktiveBelegung)[index];
                  if (!ring) return;
                  beginneBearbeitung('modulfeld', aktive.id, ring, index);
                  setMeldung('Feld ziehen oder Ecken verschieben, dann „Fertig“.');
                }}
              />
              </fieldset>
            )}
          </>
        )}
        {(() => {
          const index = SCHRITTE.findIndex(([wert]) => wert === reiter);
          const naechster = index >= 0 ? SCHRITTE[index + 1] : undefined;
          if (!naechster) return null;
          return (
            <button
              className="weiter"
              onClick={() => {
                setReiter(naechster[0]);
                seitenleiste.current?.scrollTo({ top: 0 });
              }}
            >
              Weiter: {index + 2} {naechster[1]} →
            </button>
          );
        })()}
      </aside>
    </div>
  );
}

/**
 * Verschattungsobjekte (Kapitel N, PV-N01).
 *
 * Von Hand gesetzt, mit Hoehe. Das kommt ohne gekaufte Gelaendedaten aus und
 * deckt die haeufigen Faelle ab — Baum, Nachbargebaeude, Kamin. Das
 * Gelaendemodell mit LiDAR bleibt offen, siehe README.
 */
function Verschattungsliste({
  objekte,
  zeit,
  onZeit,
  abschlaege,
  onZeichnen,
  onBearbeiten,
  inBearbeitung,
}: {
  objekte: Verschattungsobjektzeile[];
  /** Was der Schatten je Fläche kostet — sonst bleibt die Rechnung unsichtbar. */
  abschlaege: Array<{ name: string; prozent: number | null }>;
  zeit: { monat: number; tag: number; stunde: number } | null;
  onZeit: (zeit: { monat: number; tag: number; stunde: number } | null) => void;
  onZeichnen: () => void;
  onBearbeiten: (objekt: Verschattungsobjektzeile) => void;
  /** Welches Objekt wird gerade geformt — damit man es in der Liste sieht. */
  inBearbeitung: string | null;
}): React.JSX.Element {
  return (
    <div>
      <h2 style={{ marginTop: '1.5rem' }}>Verschattung</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--grau)', margin: '0 0 0.6rem' }}>
        Baum oder Nachbargebäude auf der Karte umfahren und die Höhe eintragen. Der Schattenwurf
        wird daraus gerechnet.
      </p>

      <div className="knopfreihe">
        <button onClick={onZeichnen}>Objekt zeichnen</button>
      </div>

      {objekte.map((objekt) => (
        <div key={objekt.id} style={{ borderTop: '1px solid var(--linie)', padding: '0.5rem 0' }}>
          <div className="knopfreihe" style={{ marginBottom: 0 }}>
            <input
              defaultValue={objekt.name}
              onBlur={(ereignis) =>
                void speichere('verschattungsobjekte', objekt, { name: ereignis.target.value })
              }
              style={{ flex: 1, minWidth: '6rem' }}
            />
            <select
              value={objekt.art}
              onChange={(ereignis) =>
                void speichere('verschattungsobjekte', objekt, {
                  art: ereignis.target.value,
                })
              }
              style={{ width: 'auto' }}
            >
              <option value="baum">Baum</option>
              <option value="gebaeude">Gebäude</option>
              <option value="kamin">Kamin</option>
              <option value="sonstiges">Sonstiges</option>
            </select>
            <label className="klein">
              Höhe m
              <Zahlfeld
                min={0.1}
                stellen={1}
                wert={Number(objekt.hoehe_m)}
                onWert={(hoehe) => hoehe !== null && void speichere('verschattungsobjekte', objekt, { hoehe_m: hoehe })}
                style={{ width: '5rem' }}
              />
            </label>
            <button
              aria-pressed={inBearbeitung === objekt.id}
              onClick={() => onBearbeiten(objekt)}
              title="Ecken ziehen, neue setzen"
            >
              Form
            </button>
            <button
              className="gefaehrlich"
              onClick={() => {
                if (!confirm(`${objekt.name} entfernen?`)) return;
                void speichere('verschattungsobjekte', objekt, {
                  geloescht_am: new Date().toISOString(),
                });
              }}
            >
              ×
            </button>
          </div>
        </div>
      ))}

      {/* Was das kostet. Ohne diese Zeile stehen die Objekte nur zur Zierde auf
          der Karte — und genau so war es bis 14.09.2026 auch. */}
      {objekte.length > 0 && abschlaege.some((a) => a.prozent !== null) && (
        <div className="kachel" style={{ marginTop: '0.8rem' }}>
          <h3 style={{ marginTop: 0 }}>Ertragsabschlag durch Schatten</h3>
          {abschlaege
            .filter((a) => a.prozent !== null)
            .map((a) => (
              <div
                key={a.name}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}
              >
                <span>{a.name}</span>
                <strong>−{a.prozent!.toFixed(1)} %</strong>
              </div>
            ))}
          <p className="klein" style={{ margin: '0.4rem 0 0' }}>
            Verschattete Stunden werden mit dem Sonnenstand gewichtet — früh und spät zählt
            weniger. Der Wert geht in die Ertragsrechnung und damit ins Angebot ein.
          </p>
        </div>
      )}

      {objekte.length > 0 && (
        <div className="kachel">
          <h3 style={{ marginTop: 0 }}>Schattenwurf ansehen (PV-N02)</h3>

          {/* Zwei Regler statt drei Knöpfen und einem Zahlenfeld. Der Schatten
              wandert damit beim Ziehen über das Dach — man sieht sofort, wann
              und wo er stört, statt drei Zeitpunkte einzeln durchzuklicken. */}
          <div className="knopfreihe" style={{ marginBottom: '0.4rem' }}>
            <button
              aria-pressed={zeit !== null}
              onClick={() => onZeit(zeit ? null : { monat: 12, tag: 21, stunde: 12 })}
            >
              {zeit ? 'Schatten ausblenden' : 'Schatten anzeigen'}
            </button>
          </div>

          {zeit && (
            <div className="regler">
              <label>
                <span>
                  Zeitpunkt im Jahr
                  <strong>{MONATSNAMEN[zeit.monat - 1]}</strong>
                </span>
                <input
                  type="range"
                  min={1}
                  max={12}
                  step={1}
                  value={zeit.monat}
                  onChange={(ereignis) =>
                    onZeit({ ...zeit, monat: Number(ereignis.target.value), tag: 21 })
                  }
                />
                <small>
                  Jeweils der 21. — die Sonnenwenden im Juni und Dezember sind die beiden
                  Grenzfälle, im März und September steht die Sonne dazwischen.
                </small>
              </label>

              <label>
                <span>
                  Uhrzeit
                  <strong>
                    {String(Math.floor(zeit.stunde)).padStart(2, '0')}:
                    {zeit.stunde % 1 >= 0.5 ? '30' : '00'}
                  </strong>
                </span>
                <input
                  type="range"
                  min={5}
                  max={21}
                  step={0.5}
                  value={zeit.stunde}
                  onChange={(ereignis) =>
                    onZeit({ ...zeit, stunde: Number(ereignis.target.value) })
                  }
                />
                <small>Wahre Ortszeit, nicht Sommerzeit — so rechnet die Sonne.</small>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MONATSNAMEN = [
  'Jänner',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

/** Hoehe, Seite und Sockel — das, was das Luftbild von einer Wand nicht hergibt. */
function Fassadenfelder({
  flaeche,
  onFlaeche,
}: {
  flaeche: Flaeche;
  onFlaeche: (felder: Partial<Flaeche>) => void;
}): React.JSX.Element | null {
  if (!flaeche.fassade) return null;
  const fassade = flaeche.fassade;

  return (
    <div className="feld">
      <label>Wand</label>
      <div className="knopfreihe">
        <label className="klein">
          Höhe in m
          <Zahlfeld
            min={0.5}
            max={100}
            stellen={2}
            wert={fassade.hoeheM}
            onWert={(hoeheM) => hoeheM !== null && onFlaeche({ fassade: { ...fassade, hoeheM } })}
          />
        </label>
        <label className="klein">
          Sockel in m
          <Zahlfeld
            min={0}
            max={100}
            stellen={2}
            leerErlaubt
            wert={fassade.sockelhoeheM ?? 0}
            onWert={(sockel) => onFlaeche({ fassade: { ...fassade, sockelhoeheM: sockel ?? 0 } })}
          />
        </label>
      </div>
      <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
        {(['links', 'rechts'] as const).map((seite) => (
          <button
            key={seite}
            aria-pressed={fassade.seite === seite}
            onClick={() => onFlaeche({ fassade: { ...fassade, seite } })}
          >
            Wand zeigt nach {seite}
          </button>
        ))}
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--grau)', margin: '0.35rem 0 0' }}>
        Eine Wandlinie hat zwei mögliche Normalen — die Seite entscheidet, wohin die Fassade schaut.
      </p>
    </div>
  );
}

interface Adresstreffer {
  lat: number;
  lng: number;
  bezeichnung: string;
  art: string;
}

function Projektfelder({
  projekt,
  quelle,
  vorladen,
}: {
  projekt: Projekt;
  quelle: Kartenquelle;
  vorladen: Vorladeregeln;
}): React.JSX.Element {
  const [suche, setSuche] = useState('');
  const [sucht, setSucht] = useState(false);
  const [suchfehler, setSuchfehler] = useState<string | null>(null);
  const [treffer, setTreffer] = useState<Adresstreffer[]>([]);
  const [bereitet, setBereitet] = useState(false);
  const [fortschritt, setFortschritt] = useState<Fortschritt | null>(null);
  const [bericht, setBericht] = useState<string | null>(null);
  const kachelstand = useLiveQuery(() => db.kachelstand.get(projekt.id), [projekt.id]);

  /**
   * Reisefertig machen — hier, nicht nur in der Projektliste. Wer gerade die
   * Adresse gesetzt hat, will nicht erst wieder hinausgehen, nur um die Karte
   * zu laden. Es ist derselbe Ablauf, nicht eine zweite Fassung davon.
   */
  async function bereiteVor(): Promise<void> {
    if (projekt.lat === null || projekt.lng === null) return;
    setBereitet(true);
    setBericht(null);
    try {
      const ergebnis = await bereiteProjektVor(
        { id: projekt.id, lat: projekt.lat, lng: projekt.lng },
        quelle,
        vorladen,
        setFortschritt,
      );
      setBericht(alsSatz(ergebnis));
    } finally {
      setBereitet(false);
      setFortschritt(null);
    }
  }

  async function speicherFeld(feld: keyof Projekt, wert: string): Promise<void> {
    // Nur was sich geändert hat: Wer bloß durch die Felder tippt, soll nicht
    // den eigenen, vielleicht älteren Stand über eine Änderung von einem
    // anderen Gerät schreiben.
    if ((wert || null) === ((projekt[feld] as string | null | undefined) ?? null)) return;
    await speichere('projekte', projekt, { [feld]: wert || null });
  }

  /**
   * Adressvorschläge beim Tippen (PV-D02).
   *
   * Vorher musste man raten: Adresse eintippen, Knopf drücken, und entweder
   * sprang die Karte irgendwohin oder es kam „nicht gefunden" — ob es die
   * Adresse überhaupt gibt, erfuhr man nie. Jetzt kommen die Treffer, während
   * man schreibt, und man wählt einen aus.
   *
   * Gesucht wird über den eigenen Server (siehe apps/api/src/adresse.ts):
   * Nominatim verlangt eine Kennung, die ein Browser nicht setzen darf, und
   * verträgt nur eine Anfrage je Sekunde.
   *
   * Die Schreibweisen aus adresse.ts kommen weiter zum Zug — „Puchheim Gasse"
   * und „Puchheimgasse" meinen dieselbe Gasse, und welche im Verzeichnis steht,
   * weiß niemand vorher.
   */
  useEffect(() => {
    const eingabe = suche.trim();
    if (eingabe.length < 3) {
      setTreffer([]);
      return;
    }
    // Nicht bei jedem Buchstaben losschicken — erst, wenn kurz Ruhe ist.
    const uhr = setTimeout(() => void frageAdressen(eingabe), 350);
    return () => clearTimeout(uhr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suche]);

  // Antworten kommen außer der Reihe: Der Server reiht die Anfragen an den
  // Adressdienst nacheinander. Es zählt nur die zur letzten Eingabe.
  const sucheNummer = useRef(0);
  async function frageAdressen(eingabe: string): Promise<void> {
    const nummer = ++sucheNummer.current;
    const aktuell = (): boolean => nummer === sucheNummer.current;
    setSucht(true);
    setSuchfehler(null);
    try {
      const an = await anmeldung();
      for (const variante of adressvarianten(eingabe)) {
        const antwort = await fetch(`/api/adresse?q=${encodeURIComponent(variante)}`, {
          headers: an ? { Authorization: `Bearer ${an.token}` } : {},
        });
        if (!aktuell()) return;
        if (!antwort.ok) {
          setSuchfehler('Der Adressdienst ist gerade nicht erreichbar.');
          return;
        }
        const daten = (await antwort.json()) as { treffer: Adresstreffer[] };
        if (!aktuell()) return;
        if (daten.treffer.length > 0) {
          setTreffer(daten.treffer);
          if (variante !== eingabe) {
            // Vorher stand hier "Nichts unter …" und darunter die Treffer —
            // das las sich wie ein Misserfolg, obwohl es einer ist.
            setSuchfehler(`Gefunden unter „${variante}" — die Schreibweise ist dort eine andere.`);
          }
          return;
        }
      }
      setTreffer([]);
      setSuchfehler('Keine Adresse gefunden — auch nicht in anderer Schreibweise.');
    } catch {
      if (aktuell()) setSuchfehler('Die Adresssuche braucht Verbindung.');
    } finally {
      if (aktuell()) setSucht(false);
    }
  }

  async function uebernimm(treffer: Adresstreffer): Promise<void> {
    // Strasse, Ort und Postleitzahl gleich mitnehmen — sie stehen in der
    // Projektliste und im Angebot, und niemand tippt sie gern zweimal.
    const teile = treffer.bezeichnung.split(',').map((t) => t.trim());
    const plz = teile.find((t) => /^\d{4}$/.test(t));
    await speichere('projekte', projekt, {
      lat: treffer.lat,
      lng: treffer.lng,
      strasse: teile.slice(0, 2).join(' ') || null,
      ...(plz ? { plz } : {}),
      ...(plz ? { ort: teile[teile.indexOf(plz) + 1] ?? null } : {}),
    });
    setSuche('');
    setTreffer([]);
    setSuchfehler(null);
  }

  return (
    <>
      <div className="feld">
        <label htmlFor="kunde">Kunde</label>
        <input
          id="kunde"
          defaultValue={projekt.kunde_name}
          onBlur={(e) => void speicherFeld('kunde_name', e.target.value)}
        />
      </div>
      {/* Was der Netzantrag braucht. E-Mail und Telefon gab es in der
          Datenbank seit Anfang, aber keine Maske dafuer. */}
      <div className="feldraster">
        <div className="feld">
          <label htmlFor="kundemail">E-Mail</label>
          <input
            id="kundemail"
            type="email"
            inputMode="email"
            autoCapitalize="off"
            defaultValue={projekt.kunde_email ?? ''}
            onBlur={(e) => void speicherFeld('kunde_email', e.target.value.trim())}
          />
        </div>
        <div className="feld">
          <label htmlFor="kundetelefon">Telefon</label>
          <input
            id="kundetelefon"
            type="tel"
            inputMode="tel"
            defaultValue={projekt.kunde_telefon ?? ''}
            onBlur={(e) => void speicherFeld('kunde_telefon', e.target.value.trim())}
          />
        </div>
        <div className="feld">
          <label htmlFor="kundegeburt">Geburtsdatum</label>
          <input
            id="kundegeburt"
            type="date"
            key={`geb-${tagVon(projekt.kunde_geburtsdatum) ?? ''}`}
            defaultValue={tagVon(projekt.kunde_geburtsdatum) ?? ''}
            onBlur={(e) => {
              if ((e.target.value || null) !== tagVon(projekt.kunde_geburtsdatum)) {
                void speicherFeld('kunde_geburtsdatum', e.target.value);
              }
            }}
          />
        </div>
      </div>
      <div className="feld">
        <label htmlFor="zaehlpunkt">Zählpunktnummer</label>
        <input
          id="zaehlpunkt"
          defaultValue={projekt.zaehlpunktnummer ?? ''}
          onBlur={(e) => void speicherFeld('zaehlpunktnummer', e.target.value)}
        />
      </div>
      <div className="feldraster">
        <div className="feld">
          <label htmlFor="stromlieferant">Stromlieferant heute</label>
          <input
            id="stromlieferant"
            list="stromlieferanten"
            defaultValue={projekt.stromlieferant ?? ''}
            onBlur={(e) => void speicherFeld('stromlieferant', e.target.value.trim())}
          />
        </div>
        <div className="feld">
          <label htmlFor="stromabnehmer">Gewünschter Abnehmer der Einspeisung</label>
          <input
            id="stromabnehmer"
            list="stromabnehmer-vorschlaege"
            defaultValue={projekt.stromabnehmer ?? ''}
            onBlur={(e) => void speicherFeld('stromabnehmer', e.target.value.trim())}
          />
        </div>
      </div>
      {/* Vorschlaege, keine feste Liste — es kommen laufend Anbieter dazu. */}
      <datalist id="stromlieferanten">
        {['EVN', 'Wien Energie', 'Verbund', 'Energie AG', 'Kelag', 'Salzburg AG', 'Energie Steiermark', 'oekostrom', 'MAXENERGY', 'Wels Strom', 'Energie Burgenland'].map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <datalist id="stromabnehmer-vorschlaege">
        {['OeMAG', 'Energiegemeinschaft', 'Stromlieferant (Rücknahmetarif)', 'Eigenverbrauch, keine Einspeisung'].map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <div className="feld">
        <label htmlFor="adresse">Adresse suchen</label>
        <input
          id="adresse"
          value={suche}
          placeholder={[projekt.strasse, projekt.plz, projekt.ort].filter(Boolean).join(', ')}
          onChange={(e) => setSuche(e.target.value)}
        />
        {sucht && (
          <p className="klein" style={{ margin: '0.3rem 0 0' }}>
            Sucht …
          </p>
        )}
        {suchfehler && (
          <div
            className={`hinweis ${treffer.length > 0 ? 'hinweis--gut' : 'hinweis--warnung'}`}
            style={{ marginBottom: 0 }}
          >
            {suchfehler}
          </div>
        )}

        {/* Vorschlaege waehrend des Tippens. Vorher musste man raten, ob es die
            eingegebene Adresse ueberhaupt gibt. */}
        {treffer.length > 0 && (
          <div className="vorschlagsliste">
            {treffer.map((t) => (
              <button key={`${t.lat},${t.lng}`} onClick={() => void uebernimm(t)}>
                {t.bezeichnung}
                {t.art ? <small>{t.art}</small> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* --- Reisefertig machen (PV-B01) ------------------------------- */}
      <div className="kachel">
        <h3 style={{ marginTop: 0 }}>Für die Fahrt vorbereiten</h3>
        <p className="klein" style={{ margin: '0 0 0.6rem' }}>
          Lädt Kartenkacheln, Gebäudeumrisse und Geländedaten für den Umkreis. Braucht einmal
          Verbindung — danach geht die Aufnahme vor Ort ohne Empfang. Ohne diesen Schritt findet
          „Gebäude auswählen“ nichts.
        </p>

        <div className="knopfreihe" style={{ marginBottom: bericht ? '0.6rem' : 0 }}>
          <button
            className="wichtig"
            onClick={() => void bereiteVor()}
            disabled={projekt.lat === null || bereitet}
          >
            {bereitet ? 'Lädt …' : kachelstand ? 'Erneut vorbereiten' : 'Vorbereiten'}
          </button>
          {kachelstand && !bereitet && (
            <span className={`reisefertig ${reisefertigKlasse(kachelstand)}`}>
              {reisefertigText(kachelstand)}
            </span>
          )}
        </div>

        {projekt.lat === null && (
          <p className="klein">Erst die Adresse setzen — ohne Standort gibt es keinen Umkreis.</p>
        )}
        {fortschritt && (
          <p className="klein">
            Kartenkacheln: {fortschritt.geladen} von {fortschritt.gesamt}
          </p>
        )}
        {bericht && <div className="hinweis">{bericht}</div>}
      </div>
    </>
  );
}

function anteil(kachel: { geladen: number; gesamt: number } | undefined): number {
  if (!kachel || kachel.gesamt === 0) return 0;
  return kachel.geladen / kachel.gesamt;
}

function reisefertigText(kachel: { geladen: number; gesamt: number } | undefined): string {
  const wert = anteil(kachel);
  if (wert >= 0.98) return 'Karte vollständig';
  if (wert > 0) return `Karte ${Math.round(wert * 100)} %`;
  return 'Karte fehlt';
}

function reisefertigKlasse(kachel: { geladen: number; gesamt: number } | undefined): string {
  const wert = anteil(kachel);
  if (wert >= 0.98) return 'reisefertig--ja';
  if (wert > 0) return 'reisefertig--teil';
  return 'reisefertig--nein';
}
